import type { Navigator, Screen } from "../app/screen";
import { Api, ApiError } from "../net/api";
import { GameHubClient } from "../net/hub";
import { showConnState } from "../ui/connection";
import { MatchModel, stat, type RuntimeObject } from "../match/state";
import { registerContext, clearContext } from "../app/diagnostics";
import { cardRecord, loadCardNames, cardName, hasRealm } from "../match/cards";
import { cardEl } from "../match/card";
import { legalAttackTargets, hasKeyword, roleOf } from "../match/rules";
import { guidance } from "../match/guidance";
import { choice } from "../app/session";
import { loadSettings, settingsButton, closeSettings } from "../ui/settings";
import { REALM_COLOUR } from "../card-frames.generated";
import { fireArrow } from "../fx/arrow";
import { DEFAULT_BOARD, themeFor, type BoardTheme } from "../match/boardTheme";
import { Theme } from "../theme.generated";
import { audio } from "../fx/audio";
import { announce, basinEl, clockEl, endOverlay, foxfirePips, graveyardOverlay, heroEl, heroPowerEl, opponentHandEl, pileEl } from "../match/hud";
// ⭐ Which cards must be aimed, and at what - generated from the served effect data, never from the
// card's English rules text. See match/targeting.ts for what an unaimed play silently costs.
import { aimFor, legalPlayTargets } from "../match/targeting";
// ⭐ 60 emotes were staged and unreachable until now - see match/emotes.ts.
import { attachEmoteWheel, emoteByExpr, showEmote } from "../match/emotes";
import type { EffectAim } from "../effect-keywords.generated";
import { tutorialSignal } from "../tutorial";

/**
 * THE MATCH. Connect, initialise, and render what the server says is on the table.
 *
 * ⭐ **BUILT AGAINST A CAPTURED PAYLOAD, NOT AGAINST THE DECOMPILED CLIENT.** See `match/state.ts`
 * for the contract and where it came from. The project has been caught four times deriving this by
 * reading code and being wrong in a different way each time.
 *
 * ⚠️ **THE SERVER IS AUTHORITATIVE AND THIS SCREEN DECIDES NOTHING.** It renders state and sends
 * commands. No rule, no cost check, no legality test runs here.
 *
 * ⚠️ **`TimeHash` IS ECHOED, NEVER COMPUTED.** The engine rejects any command whose hash is not the
 * timer's current one (`Revival.Core.decompiled.cs:16700`). Every "time hash isn't correct" is a
 * desync we caused.
 *
 * ⛔ **THIS IS THE BOARD READOUT, NOT THE FINISHED BOARD.** Cards are listed, not laid out; there is
 * no drag, no art and no animation yet. That is said plainly rather than dressed up - the whole
 * reason the old client burned two weeks is that "it renders" kept being reported as "it works".
 */

export const MatchScreen: Screen = {
  id: "match",

  // ⚠️ In a match the way out is CONCEDE, not BACK - walking away silently would strand the
  // opponent on their turn clock. Declared so the router's exit rule is satisfied honestly rather
  // than by exempting the screen.
  mustOffer: ["CONCEDE"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const el = document.createElement("main");
    el.className = "match";


    // ⭐ THE BOARD IS A THEME, AND THE SERVER PICKS IT. `match/boardTheme.ts` owns the list;
    // nothing here knows a theme's name, so a bought skin is a new row rather than a code change.
    //
    // 🚨 **THIS LINE USED TO READ `= DEFAULT_BOARD`, AND THAT THREW AWAY AUTHORED CONTENT.** Every
    // one of the 35 campaign stages declares an `arenaTheme`; the board ignored all of them and drew
    // the neutral arena, so five realms looked identical. Same shape as the 35 unreachable stages -
    // content that is served, and that no surface consumes.
    //
    // ⚠️ It is applied ASYNCHRONOUSLY, so the board starts on the default and swaps when the session
    // answers. Blocking the board on a network call to decide its BACKGROUND would trade a real
    // delay for a cosmetic, and a failed call would leave a player staring at nothing.
    let theme: BoardTheme = DEFAULT_BOARD;
    const paintTheme = (t: BoardTheme): void => {
      el.style.setProperty("--board-surface", `url("${t.surface}")`);
      el.style.setProperty("--board-inset", `${t.inset * 100}%`);
      el.style.setProperty("--board-scrim", String(t.scrim));
      el.dataset["board"] = t.id;   // the gate reads this; never read for presentation
    };
    paintTheme(theme);
    void Api.activeSession().then((s) => {
      const next = themeFor((s as { arenaTheme?: string | null })?.arenaTheme);
      if (next.id !== theme.id) { theme = next; paintTheme(next); }
    }).catch(() => { /* the default board is already painted - a duel has no realm */ });

    host.append(el);

    // ⭐ THE OPPONENT'S MOVES, IN WORDS. See `noteOpponentAction`.
    const feed = document.createElement("div");
    feed.className = "match__feed";
    feed.setAttribute("aria-live", "polite");

    const board = document.createElement("div");
    board.className = "board";

    // ⭐ **THE PHASE RAIL - STAGE AND INSTRUCTION, ALWAYS BOTH.** The mulligan had its own screen in
    // the original; folding it onto the board removed the context a dedicated screen gives for free,
    // so the board has to say it out loud. See `match/guidance.ts`.
    const status = document.createElement("div");
    status.className = "match__turnstate";
    const stageLine = document.createElement("p");
    stageLine.className = "match__stage";
    const actionLine = document.createElement("p");
    actionLine.className = "match__instruction";
    status.append(stageLine, actionLine);

    /**
     * Say something in the rail without destroying it.
     *
     * 🚨 **FIVE PLACES USED TO SET `status.textContent` DIRECTLY, WHICH DELETES BOTH CHILD
     * ELEMENTS.** The rail then rendered as one bare sentence and the STAGE vanished - visible in
     * the very first capture after it was built. A container that holds structure must never be
     * written to as if it held text.
     *
     * ⚠️ A refusal is the case that matters most. The engine swallows illegal commands, so if the
     * client does not say why, the player sees a click that did nothing.
     */
    function say(stage: string, action: string, problem = false): void {
      stageLine.textContent = stage;
      actionLine.textContent = action;
      status.classList.toggle("match__turnstate--problem", problem);
    }

    // ⚠️ Debug telemetry, behind the flag, and OUT of the player's layout entirely.
    const dump = new URLSearchParams(location.search).has("matchdump");
    const debugLine = document.createElement("p");
    debugLine.className = "match__debug";
    debugLine.hidden = !dump;

    const concede = document.createElement("button");
    concede.type = "button";
    concede.className = "utilbtn match__concede";
    concede.dataset["label"] = "CONCEDE";

    // ⭐ SOUND, REACHABLE FROM THE BOARD. Alex asked for it on the game page specifically, and he is
    // right: the moment a player wants the music down is the moment it is playing, not two screens
    // away in a lobby. It is an OVERLAY, never a route - leaving a live board to change the volume
    // would abandon the turn.
    const soundBtn = settingsButton();
    soundBtn.classList.add("match__sound");
    void loadSettings();
    concede.textContent = "CONCEDE";
    // 🚨 **CONCEDING USED TO JUST WALK AWAY, AND THE SERVER NEVER KNEW.** It stopped the socket and
    // navigated to the menu: the match was ABANDONED, not conceded. No result was recorded, the
    // player's losses never moved, and the session sat on the server until it was reaped.
    //
    // ⭐ The original sends the engine's own `GameEndCmd` with `{ Reason: "Surrender" }` and the
    // current TimeHash (`SendSurrender`, Revival.Client :58224). That is the shipped rail; use it.
    //
    // ⚠️ **AND IT ASKS FIRST.** Conceding is irreversible and the control sits beside the board, one
    // slip from END TURN. The original opens a confirmation dialog; a two-step button is the same
    // guard without a modal over a live game.
    let conceding = false;
    let concedeTimer: number | null = null;
    concede.addEventListener("click", () => {
      if (!conceding) {
        conceding = true;
        concede.textContent = "CONFIRM?";
        concede.classList.add("match__concede--armed");
        // ⚠️ Disarms itself. A button left saying CONFIRM? becomes a trap on the next stray click.
        concedeTimer = window.setTimeout(() => {
          conceding = false;
          concede.textContent = "CONCEDE";
          concede.classList.remove("match__concede--armed");
        }, 4000);
        return;
      }
      if (concedeTimer !== null) window.clearTimeout(concedeTimer);
      send("GameEndCmd", { MetaJson: JSON.stringify({ Reason: "Surrender" }) });
      concede.disabled = true;
      concede.textContent = "CONCEDED";
      // ⭐ **STAY AND LET THE MATCH FINISH.** Navigating away here is what made a concession
      // invisible: the command went out, the player was already on the menu when `EndGame` came
      // back, and the end card rendered onto a screen nobody was looking at. The overlay IS the
      // exit - it offers LEAVE and PLAY AGAIN.
      //
      // ⚠️ **WITH A FLOOR UNDER IT.** If the server never answers, the player must not be stranded
      // on a board they have already given up. Four seconds, then out.
      window.setTimeout(() => {
        if (!model.ended) {
          void hub?.stop();
          nav.go("menu");
        }
      }, 4000);
    });

    const act = document.createElement("button");
    act.type = "button";
    act.className = "plate match__act";
    act.hidden = true;
    const actFace = document.createElement("span");
    actFace.className = "plate__face";
    actFace.setAttribute("aria-hidden", "true");
    const actLabel = document.createElement("span");
    actLabel.className = "plate__label";
    act.append(actFace, actLabel);

    const tray = document.createElement("div");
    tray.className = "tray";

        // ⭐ The two board-level controls live in ONE row, so neither has to guess the other's width.
    const tools = document.createElement("div");
    tools.className = "match__tools";
    tools.append(soundBtn, concede);

    el.append(status, feed, board, tray, tools, act, debugLine);

    const model = new MatchModel();
    let readyToPlaySent = false;

    /*
      ⭐ **TELL THE BOARD WHO WE ARE, BEFORE THE SNAPSHOT DECIDES FOR US.**

      🔴 In PvP both seats are human, so the board's old rule - *we are the player who is not the bot* -
      cannot tell the two apart and hands BOTH players the same person's hand. `MatchModel.accountId`
      is the fix and this is where it is filled.

      ⚠️ **NOT AWAITED, AND `resolveSelf` IS RE-RUN WHEN IT LANDS.** Blocking the mount on a REST call
      would delay the hub connection and the snapshot behind it, which is the one thing this screen
      must not be slow at. So the fetch races the snapshot and whichever arrives second re-resolves;
      the model logs loudly if it ever has to guess between two humans.
    */
    void Api.userData()
      .then((u) => {
        const id = (u as { id?: string })?.id;
        if (!id) return;
        model.accountId = id;
        model.reresolveSelf();
      })
      .catch(() => { /* the !IsBot fallback still covers every single-player match */ });

    /**
     * ⭐ **WHAT THE BOARD WAS DOING, FOR THE BUG REPORTER.** Alex, 2026-08-17: *"The in game bug
     * reporter should copy the state and page the game is on when a user clicks and fills it in."*
     *
     * ⚠️ **A PROVIDER, NOT AN EXPORTED MODEL.** `model` is deliberately a `const` inside this
     * mount; promoting it to a module global so a diagnostic could read it would make a second owner
     * of live match state, and this screen has already paid for that class of bug. The reporter
     * calls this closure instead and gets a flat, already-summarised object.
     *
     * ⭐ `unhandled` is the highest-value field here and is the reason this is worth wiring at all:
     * it names the engine events this client saw and did not apply. A board that is quietly wrong
     * because of a missing handler is indistinguishable from a rules bug to the player reporting it,
     * and this turns that report into the answer.
     */
    registerContext("match", () => ({
      snapshotSeen: model.snapshotSeen,
      reinitialize: model.reinitialize,
      selfId: model.selfId,
      turn: model.timer.Turn ?? null,
      round: model.timer.Round ?? null,
      timerState: model.timer.State ?? null,
      myTurn: model.timer.OwnerId ? model.timer.OwnerId === model.selfId : null,
      objects: model.objects.size,
      ended: model.ended,
      players: model.players.map((pl) => ({
        name: pl.UserName ?? null,
        isBot: pl.IsBot ?? null,
        isSelf: pl.UserId === model.selfId,
        foxfire: stat(pl.Mana),
        hand: stat(pl.HandCount),
      })),
      unhandledEvents: Object.fromEntries(model.unhandled),
    }));

    /**
     * Cards the player has marked to REPLACE during the mulligan.
     *
     * 🚨 **THE MULLIGAN EXISTS TO LET A PLAYER SWAP CARDS, AND THE FIRST VERSION OF THIS SCREEN
     * OFFERED NO WAY TO.** Alex, 2026-08-14: *"you must follow the rules of the game, at this stage
     * the player can choose to swap out cards"* and *"I do not see that option on this page."* The
     * screen rendered five real cards and was still wrong, because it implemented the SURFACE and
     * not the RULE. That is the check to run before calling any screen done: name the rule, and
     * make sure a player can perform it.
     */
    const swapping = new Set<number>();

    /**
     * The creature currently selected to attack with, if any.
     *
     * ⚠️ **A CREATURE MAY ATTACK ONLY WHILE IT HAS A MOVE LEFT.** `PerformAttackCmd` re-checks with
     * `IsCanAttack` and throws `ImNotAvailable`; `MoveCount` is what the board shows a player, so an
     * exhausted creature is not offered as an attacker rather than being refused after the click.
     */
    let attacker: number | null = null;

    /**
     * True while the player is choosing a target for the hero power.
     *
     * 🚨 **THE POWER NEEDS A TARGET AND THE FIRST VERSION SENT NONE.** "Restore 2 Health. If the
     * target is a unit, give it +1 Health" - the engine resolves nothing without one, so the button
     * spent no foxfire and did nothing, which is precisely the silent-refusal defect this rebuild
     * exists to remove. Pressing the power now ARMS it; the next click on a valid target fires it.
     *
     * ⚠️ The original guards on exactly this too: `GetAvailableAbility` requires
     * `!GameBus.TargetSelecting`, so a power cannot be armed twice.
     */
    let powerArmed = false;

    /**
     * The last turn we announced, so §1.11's overlay fires ONCE when the turn becomes ours.
     *
     * ⚠️ Keyed on the TURN NUMBER, not on a boolean. `ourTurn()` is true for every event of our
     * turn, so a boolean would re-announce on each one - a banner that flashes on every card played.
     */
    let announcedTurn = -1;

    /** The victory/defeat sting plays once, not on every render of a finished board. */
    let endSounded = false;

    /**
     * The turn our hero power was last used on.
     *
     * ⚠️ **A LOCAL GUARD, NOT THE RULE.** The engine owns whether a power still has a charge
     * (`EffectState.Length`); this only stops the obvious double-press inside one turn so the
     * player is not invited into a refusal. When the two disagree the SERVER is right, and its
     * refusal is shown.
     */
    let powerUsedOnTurn = -1;

    /**
     * Animations in flight, by object id, with the moment they started.
     *
     * 🚨 **THE BOARD RE-RENDERS ON EVERY SERVER EVENT, WHICH WIPED EVERY ANIMATION MID-FLIGHT.**
     * That is why Alex kept reporting *"the attack animations still are not working"* while the
     * classes were being applied correctly: `board.replaceChildren(...)` destroys the element the
     * class is on, and a hit arrives in the same batch as the state change that triggers the
     * re-render - so the animation lived for part of one frame.
     *
     * ⭐ Keeping them here and RE-APPLYING after each render is what makes them survive. A 700ms
     * window covers the longest of them (the death fade at 520ms) without an animation reappearing
     * later on a recycled element.
     */
    const inFlight = new Map<number, { cls: string; at: number }>();

    /**
     * TYPED FOXFIRE, from the server.
     *
     * 🚨 **THE COLOUR GATE IS A LAUNCH BLOCKER IF THE PLAYER CANNOT SEE IT.** `Foxfire.cs` says so
     * outright: *"Enforcing a colour gate the player cannot see means refusing a play for a reason
     * nothing on screen explains, which is indistinguishable from a bug and will be reported as
     * one."* So the pool and each card's requirement are rendered, not inferred.
     *
     * ⭐ **AND `playable` COMES FROM HERE, NOT FROM COMPARING MANA.** The server's own
     * `Foxfire.CanPlay` applies BOTH gates - the engine's scalar cost and our additive colour
     * requirement. A client-side `cost <= pool` check knows only half the rule and would invite a
     * player to press a card the server then refuses.
     */
    interface FoxfireHandCard {
      objectId?: number;
      colour?: string;
      requires?: Record<string, number> | null;
      playable?: boolean;
      reason?: string;
    }
    let foxfire: {
      enabled?: boolean;
      inMatch?: boolean;
      pool?: Record<string, number>;
      hand?: FoxfireHandCard[];
      isYourTurn?: boolean;
    } = {};

    /**
     * TURN HEADERS, so the record reads as a game rather than as a list of sentences.
     *
     * ⭐ Alex asked for *"the players names and turn 1, turn 2"*. Both come from the engine's own
     * timer (`Turn`, `OwnerId`), never from counting renders - a render happens many times a turn and
     * a counter here would drift the first time one is skipped.
     */
    /* ── THE AFK WARNING ───────────────────────────────────────────────────────────────────────
     * 🚨 **ALEX, 2026-08-18:** *"if the player fails to click end turn in the 60 seconds then they
     * lose, reason for loss AFK"*, then, on being told the engine already allows three: *"If you
     * want to make it 3 misses on the 1 min timer that is fine but there needs to be a warning -
     * then, or giving 3 chances means nothing."*
     *
     * ⭐ He is exactly right: an allowance nobody is told about is not an allowance, it is a trap
     * with a delay on it. `afkTurnCountToLose` stays at the engine's 3 and each miss now SAYS SO.
     *
     * ⚠️ **THIS COUNTER WARNS; IT DOES NOT DECIDE.** The engine's `CheckAfk` owns the actual
     * forfeit, and there is no per-miss event on the wire to mirror it (only `LogicEvents` and
     * `GameAction` exist). So this is inferred locally - our turn ended and we never pressed END
     * TURN - and if the two ever disagreed the WARNING would be wrong while the LOSS stayed right.
     * That is the correct way round for a derived number, and it is why the copy says "you may
     * forfeit" rather than quoting a precise remaining count as fact. */
    /* ── THE MATCH IS GONE AND THE BOARD IS STILL SITTING THERE ────────────────────────────────
     * 🚨 **ALEX, 2026-08-18: *"see screenshot the game is broken completely now"*.** His capture
     * shows the board still drawn, his hero on 2 HP, the clock on 0, and the rail reading:
     *
     *     That card cannot be burned: {"error":"no active match"}
     *
     * ⛔ **THE SERVER HAD NO SESSION AND THE CLIENT DID NOT NOTICE.** Every action then fails with
     * the same message, the turn clock sits at zero, and the only control that still does anything
     * is CONCEDE - on a match that no longer exists. It reads as a total freeze because, from the
     * player's side, that is exactly what it is.
     *
     * ⭐ **AN ERROR THAT MEANS "THE SESSION IS OVER" IS NOT A TOAST, IT IS AN END STATE.** Any
     * command coming back with it now ends the board honestly and offers the two ways out, instead
     * of printing a JSON blob into the guidance line and waiting for the player to give up. */
    let matchGoneShown = false;
    function isMatchGone(e: unknown): boolean {
      return /no active match|match not found|session (?:has )?ended/i.test(describe(e));
    }
    function showMatchGone(): void {
      if (matchGoneShown) return;
      matchGoneShown = true;
      el.classList.add("match--over");
      void hub?.stop();
      el.append(endOverlay(
        false,
        "This match is no longer running on the server, so no further move can be made.",
        () => { void hub?.stop(); nav.go("match"); },
        () => { void hub?.stop(); nav.go("menu"); },
      ));
    }

    const AFK_LIMIT = 3;
    let endedByChoice = false;
    let missedTurns = 0;
    let watchedTurn = -1;
    let wasOurTurn = false;

    let loggedTurn = -1;
    function logTurnHeader(): void {
      const t = model.timer;
      const turn = Number(t?.Turn ?? -1);
      if (turn === loggedTurn) return;
      loggedTurn = turn;
      if (turn < 0) return;

      /* 🔴 **`Turn` COUNTS EVERY PLAYER'S GO, SO IT IS DOUBLE WHAT A PLAYER CALLS A ROUND.** Alex,
         2026-08-18: *"it says turn 2 but it is turn 1."* He is right and the engine is right - they
         are counting different things. The bot moving and then the player moving is ONE round to a
         person and TWO turns to the engine, so the log said TURN 2 on what anybody would call the
         first round.
         ⭐ The engine already carries `Round`. Show the number the player is counting. */
      const round = Number(t?.Round ?? 0);

      const owner = String(t?.OwnerId ?? "");
      const who = model.players.find((p) => p.UserId === owner);
      const label = owner === model.selfId
        ? (who?.UserName ?? "You")
        : (who?.UserName ?? "Opponent");

      const h = document.createElement("span");
      h.className = "match__feedturn";
      h.dataset["side"] = owner === model.selfId ? "ours" : "theirs";
      h.textContent = `ROUND ${round > 0 ? round : Math.ceil((turn + 1) / 2)} · ${label}`;
      /* ⭐ **NEWEST FIRST.** Alex, 2026-08-18: *"the newest plays must be at the top of it and the
         older plays pushed down, now it is backwards so you only see the first rounds."* He is
         right and the symptom is worse than an ordering preference: appended oldest-first, the log
         grew downward past the bottom of the screen, so the ONLY part ever visible was the opening
         turns - the least useful moment in the match. */
      feed.prepend(h);
    }

    /**
     * Follow the newest line, UNLESS the player has scrolled up to read.
     *
     * ⚠️ Auto-scrolling someone who is reading history is the defect that makes a live log unusable,
     * so it only pins to the bottom when it was already at the bottom.
     */
    function keepScrolled(): void {
      // ⚠️ Newest is at the TOP now, so "follow the newest" means pinning to the top - and only when
      // the player has not scrolled down to read history.
      if (feed.scrollTop < 40) feed.scrollTop = 0;
    }

    /**
     * One line of plain English for something the opponent just did.
     *
     * ⚠️ **NAMED FROM THE BOARD, NOT INVENTED.** The card's own title is looked up from the runtime
     * object; a generic "a creature attacked" would be exactly the ambiguity the testers are asking
     * us to remove.
     */
    function noteAction(v: { kind: string; id: number; amount?: number; by?: number }): void {
      const nameOf = (id: number | undefined): string => {
        if (typeof id !== "number") return "";
        const o = model.objects.get(id);
        if (!o) return "";
        if (o.Type === "Hero") return o.OwnerUserId === model.selfId ? "your hero" : "their hero";
        return cardName(o.DataId) || "a card";
      };
      let text = "";
      if (v.kind === "spawn") {
        const n = nameOf(v.id);
        if (n) text = `Played ${n}`;
      } else if (v.kind === "hit") {
        const target = nameOf(v.id);
        const src = nameOf(v.by);
        if (target) {
          text = src
            ? `${src} hit ${target} for ${v.amount ?? 0}`
            : `${target} took ${v.amount ?? 0} damage`;
        }
      } else if (v.kind === "death") {
        const n = nameOf(v.id);
        if (n) text = `${n} was destroyed`;
      }
      if (!text) return;

      logTurnHeader();

      const line = document.createElement("span");
      line.className = "match__feedline";
      line.textContent = text;
      /* ⚠️ **AFTER THE HEADER, NOT AT THE VERY TOP.** Newest-first at the TURN level, oldest-first
         WITHIN a turn: a turn's actions still read in the order they happened, which is what makes
         "it played this, then attacked with that" legible. Reversing both would scramble cause and
         effect inside every turn. */
      const head = feed.querySelector(".match__feedturn");
      if (head && head.nextSibling) feed.insertBefore(line, head.nextSibling);
      else if (head) feed.append(line);
      else feed.prepend(line);

      /* 🚨 **IT USED TO CAP AT FOUR LINES AND DELETE EACH ONE AFTER SIX SECONDS.** Alex, 2026-08-18:
         *"this should not disappear, it needs to keep the ongoing record, and it needs to separate
         with the players names and turn 1, turn 2, so a player can scroll through and review it."*

         ⭐ **A FLASH MESSAGE AND A RECORD ARE DIFFERENT JOBS AND THE FLASH MESSAGE WAS WINNING.** On
         a board where the bot's entire turn arrives in one engine dispatch, six seconds is the whole
         window in which a player could have understood what happened - and it expired whether or not
         they were looking. Keeping it costs nothing and answers "what did it just do to me?" after
         the fact, which is the only time anyone actually asks. */
      keepScrolled();
    }

    async function refreshFoxfire(): Promise<void> {
      try {
        foxfire = (await Api.foxfire()) as typeof foxfire;
      } catch {
        // ⚠️ NOT FATAL. A missing HUD degrades the board; a throw here would take the match down.
        // It IS reported, because a silently absent colour gate is the failure this exists to stop.
        console.warn("[foxfire] state unavailable - the colour gate will not be shown");
      }
      render();
    }
    // Card TITLES come from the served database, so a renamed card needs no client release.
    void loadCardNames();

    try {
      // ⚠️ START THE MATCH OVER REST FIRST. The hub binds to a session that must already exist -
      // `GameHub.Bind()` logs "has no active match" and returns null otherwise, and the client then
      // waits forever on a socket that is connected and useless.
      // ⭐ **THE DECK THE PLAYER ACTUALLY CHOSE.** Without this the server falls back to
      // `Decks[0]` and every match is the same deck - the reason the game "felt wrong" while every
      // rule in it was correct. `app/session.ts` carries the choice from the PLAY screen.
      const picked = choice().deckId;

      // 🚨 **THIS SCREEN USED TO START A PRACTICE MATCH UNCONDITIONALLY, WHICH DESTROYED THE MATCH
      // THE PLAYER HAD JUST STARTED.** Measured in the server log 2026-08-14:
      //
      //     [registry] created 49c5... mode=Campaign  vs Cole North - Opening Trial
      //     [registry] replacing existing session 49c5...
      //     [registry] created 0b2c... mode=Practice   vs Starter Deck - Mara Vail
      //
      // Campaign, Gauntlet and Duel all create the session over REST and THEN navigate here. Calling
      // `practiceStart` on arrival replaced every one of them - so the stage's champion, its
      // modifiers, its arena theme AND its progression were all thrown away, and a campaign win was
      // recorded as a practice game. Nothing failed: a perfectly good match started, against the
      // wrong opponent, crediting nothing.
      //
      // ⭐ **SO THE BOARD JOINS AN EXISTING MATCH AND ONLY CREATES ONE WHEN THERE IS NONE.** The
      // server's `ActiveSession` uses `FindLive`, so a finished match never answers here and cannot
      // wedge a player out of starting a new one.
      let joining = false;
      try {
        const live = await Api.activeSession() as { id?: string };
        joining = typeof live?.id === "string" && live.id.length > 0;
      } catch {
        // ⚠️ Unreachable server: fall through and try to start one. Refusing to play because we
        // could not ASK whether a match exists would turn a blip into a dead screen.
      }
      if (!joining) {
        if (picked) await Api.selectDeck(picked);
        await Api.practiceStart(picked, choice().difficulty, choice().specialRule);
      }
      say("CONNECTING", joining ? "Joining your match." : "Reaching the match server.");

      hub = new GameHubClient();
      /* ⭐ **TELL THE PLAYER WHEN THE CONNECTION GOES.** Wired at the ONE place a hub is created, so
         there is no second site that could forget. Before 2026-08-22 a drop was a `console.warn` and
         the board simply stopped responding - indistinguishable from a frozen game. See
         `ui/connection.ts` and `docs/SCREEN-COVERAGE.md`. */
      hub.onConnState(showConnState);
      hub.onSignal((name, payload) => {
        model.applySignal(name, payload);
        // ⭐ FEED THE TUTORIAL. Its in-match chain advances on engine signals rather than on a
        // timer, so a lesson never runs ahead of the board it is describing. Safe for every signal:
        // the runner matches by name and ignores the rest.
        tutorialSignal(name);

        // ⭐ **THE STEP THAT DEALS THE HANDS.** Their client does exactly this on the opening
        // snapshot, and nothing happens without it - see `MatchModel.snapshotSeen`. Sent ONCE: the
        // browser can open the hub twice, so a repeat would run `SessionStart` again.
        if (model.snapshotSeen && !model.reinitialize && !readyToPlaySent) {
          readyToPlaySent = true;
          send("ReadyToPlayCmd");
        }

        // ⚠️ Debug-only: exposes ONE sample of each event we do not yet apply, so its shape can be
        // read off the wire rather than guessed from the decompiled client.
        if (new URLSearchParams(location.search).has("matchdump")) {
          (window as unknown as { __revivalUnhandled?: Record<string, unknown> }).__revivalUnhandled =
            Object.fromEntries(model.unhandledSample);
        }

        render();
        void refreshFoxfire();
      });

      /*
       * ⛔ **THE BOARD THAT NEVER ARRIVES, AND THE LOOP IT CREATES.**
       *
       * Nothing on this screen happens before the opening snapshot. If it never comes, the player
       * sits on an empty board - and every exit we had went back over the hub that just failed to
       * deliver it: CONCEDE sends `GameEndCmd`, game-over needs the engine, and the idle reaper is
       * THIRTY MINUTES away. Worse, `Lobby/ActiveSession` keeps handing this same match back and the
       * client auto-rejoins it, so leaving and returning lands them right back here.
       *
       * 🔴 A real player, 2026-08-17: *"i am stuck in a loop of this dule with a bot. I can not make
       * or enter a new Duel."* Their diagnostic: `snapshotSeen: false`, `objects: 0`, `selfId: ""`.
       * Nothing had arrived, and there was nothing they could press.
       *
       * ⭐ **SO THE ESCAPE GOES OVER HTTP, NOT THE HUB** (`Api.abandonSession`). It works precisely
       * when the hub does not, which is the only condition under which this control is ever shown.
       *
       * ⚠️ **12 SECONDS, AND ONLY WHEN NOTHING AT ALL HAS ARRIVED.** A slow snapshot is common; a
       * snapshot that never comes is the defect. `snapshotSeen` flips on the FIRST one, so this can
       * never fire on a live board, however slow the player or the turn.
       */
      const STUCK_AFTER_MS = 12_000;
      window.setTimeout(() => {
        if (model.snapshotSeen || model.ended) return;
        say("This duel is not responding",
            "Nothing has arrived from the server. You can leave it and start a new one.", true);
        const escape = document.createElement("button");
        escape.type = "button";
        escape.className = "utilbtn match__escape";
        escape.textContent = "LEAVE THIS DUEL";
        escape.addEventListener("click", () => {
          escape.disabled = true;
          escape.textContent = "LEAVING…";
          /* ⚠️ Navigate on BOTH arms. If the call fails the player must still get out - stranding
             them here on a failed escape is the exact bug this control exists to end. The server
             reaps the session on idle regardless, so the worst case is the 30 minutes we already
             had, not a new one. */
          void Api.abandonSession()
            .catch(() => { /* out anyway - see above */ })
            .finally(() => { void hub?.stop(); nav.go("menu"); });
        });
        status.append(escape);
      }, STUCK_AFTER_MS);

      // ⭐ THE BATTLE THEME. Ours - `MainTheme`/`BattleTheme`, not the `_THEIRS_` pair.
      audio.arm();
      audio.setMusic("battle");

      await hub.connect();
      await hub.readyToInitialize();
      say("DEALING", "Waiting for the opening hands.");
    } catch (e) {
      // ⛔ NAME THE STAGE. "Something went wrong" for every cause is what cost days on the old client.
      say("CANNOT START", `The match did not start: ${describe(e)}`, true);
      status.classList.add("page__note--error");
      return;
    }

    function render(): void {
      if (model.players.length === 0) return;

      const me = model.self();
      const them = model.opponent();

      // ⭐ **THE ARENA IS THE TABLE, NOT A WALLPAPER.** Alex, 2026-08-14: *"this table is completely
      // useless beyond a background image."* He was right - the play area floated over the painting
      // instead of using it. The ring is now the play surface, the compass rose at its heart is the
      // Foxfire basin, the poles carry the heroes and the flanks carry the piles.
      const centreEl = centre();
      // §1.17 THE BASIN, at the centre of the ring: you feed the fire at the heart of the arena.
      /* ⭐ The basin arms on drop and burns only on a second click; it needs the card's NAME to
       * ask the question usefully. See `basinEl`. */
      centreEl.append(basinEl(
        (objectId) => sacrificeById(objectId),
        (objectId) => {
          const o = model.objects.get(objectId);
          const rec = o?.DataId !== undefined ? cardRecord(o.DataId) : undefined;
          return rec ? String(rec["title"] ?? "") || undefined : undefined;
        },
      ));

      // ⚠️ **DECK RIGHT, GRAVEYARD LEFT, TIMER RIGHT** - the original's positions. A returning
      // player should not have to relearn where their own deck is.
      const railL = document.createElement("aside");
      railL.className = "rail rail--left";
      railL.append(
        pileEl("graveyard", model.zone(them?.UserId, "InDiscard").length,
               () => openGraveyard(them?.UserId, them?.UserName ?? "OPPONENT")),
        pileEl("graveyard", model.zone(me?.UserId, "InDiscard").length,
               () => openGraveyard(me?.UserId, "YOUR GRAVEYARD")),
      );

      const railR = document.createElement("aside");
      railR.className = "rail rail--right";
      /* 🚨 **THE CLOCK USED TO LIVE HERE, BETWEEN THE TWO DECK PILES.** Alex, 2026-08-18: *"the end
         turn button and the timer should be closer together... I think that basic user logic is
         something the UI/UX skills should have picked up."*
         
         He is right, and it explains why he twice said *"still no timer"* while looking at a live
         countdown: it sat vertically centred on the right edge, equidistant from everything,
         touching nothing it relates to. **A number's meaning comes largely from what it is next
         to** - beside two deck counts it reads as a third count. The clock is a deadline on ONE
         action, so it belongs against the control that performs that action. */
      railR.append(
        pileEl("deck", model.zone(them?.UserId, "InDeck").length),
        pileEl("deck", model.zone(me?.UserId, "InDeck").length),
      );

      board.replaceChildren(
        railL,
        railR,
        // ⚠️ COUNTS ARE DERIVED FROM THE OBJECTS, never from the separate HandCount/DeckCount
        // events. A count maintained alongside the thing it counts is a second owner that drifts.
        // ⭐ THE OPPONENT'S HAND, FACE DOWN, ARCING OVER THE TOP - the original's own arrangement.
        // How many cards they hold is public information and it is how you judge whether to commit.
        opponentHandEl(model.zone(them?.UserId, "InHand").length),
        side(model.zone(them?.UserId, "InTable"), true),
        centreEl,
        side(model.zone(me?.UserId, "InTable"), false),
      );

      // ⚠️ The hand and the Foxfire rail are the player's OWN furniture and sit in a band under the
      // arena, not inside it. Putting the hand in the board grid made the arena shrink every time a
      // card was drawn.
      tray.replaceChildren(foxfireHud(), hand());

      // ⭐ THE ONE ACTION, LABELLED BY THE PHASE THE SERVER SAYS WE ARE IN. Never by a guess: the
      // mulligan and the turn are different commands and the timer names which is running.
      const phase = model.timer.State ?? "";
      if (phase === "Mulligan" && !model.self()?.IsFinishedMulligan) {
        act.hidden = false;
        act.dataset["label"] = "DONE";
        actLabel.textContent = swapping.size
          ? `REPLACE ${swapping.size}`
          : "DONE";
        act.onclick = () => {
          audio.play("shuffle");
          confirmMulligan();
        };
      } else if (phase === "Ended" || model.ended) {
        // ⭐ **THE ENGINE SAYS WHO WON - `EndGame` carries WinnerId.** Reading hero HP instead would
        // call a concession, a timeout and a disconnect all wins, because both heroes are standing.
        const won = model.ended ? model.ended.winnerId === model.selfId : false;
        if (!endSounded) {
          endSounded = true;
          audio.play(won ? "victory" : "defeat");
          audio.setMusic("menu");
          // ⚠️ **THE BOARD'S OWN CONTROLS ARE MEANINGLESS NOW, AND MUST GO.** They sit outside the
          // overlay's stacking context, so they stayed live behind it: SOUND collided with the
          // disabled CONCEDE, and the action plate offered a second LEAVE that did something
          // slightly different. Two exits with one meaning is worse than one.
          el.classList.add("match--over");
          // The board is finished; the moment belongs to the player, not to another render.
          /* ⭐ **SAY WHY, WHEN THE ENGINE SAYS WHY.** Alex, 2026-08-18: *"if the player fails to
             click end turn in the 60 seconds then they lose, reason for loss AFK."* The engine
             already publishes a reason on `EndGame`; the board threw it away and showed the same
             generic line for a concede, a timeout and a real defeat. A loss the player cannot
             explain is one they read as a bug - and an AFK loss with no reason given is the most
             confusing of the three, because nothing on screen happened at all. */
          const reason = String(model.ended?.reason ?? "");
          const afk = /afk/i.test(reason);
          const words = afk
            ? (won
                ? "Your opponent ran out of time. The turn clock is 60 seconds."
                : "You ran out of time. Press END TURN before the clock reaches zero.")
            : guidance(model, 0).action.replace(" Press LEAVE to return to the menu.", "");
          el.append(endOverlay(
            won,
            words,
            () => {
              /* 🔴 **THIS SAID `nav.go("leagues")` UNDER A COMMENT PROMISING A FRESH MATCH.** Alex,
                 2026-08-18: *"i just lost a game and the options were leave or play again, i clicked
                 play again but was sent to the Quick Play lobby. I should have stayed in the game
                 and the game dealt a new hand and started over. Also I was in a practice game so
                 being sent to the Quick Play lobby is also wrong."*

                 ⭐ **THE COMMENT WAS ALREADY RIGHT AND THE CODE DISAGREED WITH IT** - which is the
                 worst pairing, because the comment is what a reader checks. `router.show()` unmounts
                 and remounts unconditionally, even for the same id, so re-entering `#/match` really
                 does start a new session; the promise was one line away from being true.

                 ⚠️ The MODE comes from `session.choice()`, which is module state and untouched by
                 the match ending - so the deck, the bot difficulty and any special rule all carry
                 over, and a practice match restarts as practice. A CAMPAIGN stage is the exception:
                 it is started through `campaignStartStage`, so it needs its stage re-sent; PLAY
                 AGAIN there gives an ordinary match of the same deck rather than that stage. */
              void hub?.stop();
              nav.go("match");
            },
            () => {
              void hub?.stop();
              nav.go("menu");
            },
          ));
        }
        // ⭐ THE WAY OUT. A finished match with no exit is a dead end, and `RevivalPage.MustOffer`
        // exists precisely because one shipped once (THE LEAGUES, no PLAY and no BACK).
        act.hidden = false;
        act.dataset["label"] = "LEAVE";
        actLabel.textContent = "LEAVE";
        act.onclick = () => nav.go("menu");
      } else if (aiming || powerArmed) {
        /* ⭐ **A CONTROL, NOT A SENTENCE.** The only way out of aiming was "Press ESC to cancel" in
           the instruction line - which is body copy on a game board, and a player who does not read
           it is stuck with a board that ignores every click. The action plate is already the one
           place that says what to do next, so while the board is aiming it says CANCEL. */
        act.hidden = false;
        act.dataset["label"] = "CANCEL";
        actLabel.textContent = "CANCEL";
        act.onclick = () => { if (aiming) { resolveAim(null); } else { powerArmed = false; render(); } };
      } else if (model.ourTurn()) {
        act.hidden = false;
        act.dataset["label"] = "END TURN";
        actLabel.textContent = "END TURN";
        act.onclick = () => { endedByChoice = true; send("PassTurnCmd"); };
      } else {
        act.hidden = true;
        act.onclick = null;
      }

      // ⭐ §5 COMBAT ANIMATION, DRAINED AFTER THE BOARD IS DRAWN.
      // ⚠️ AFTER, because the elements it animates must exist. Draining before the render would
      // silently drop every event on the first frame of a new board - which is exactly the frame a
      // kill happens on.
      playVisuals();

      /* §1.11 THE TURN HAND-OFF OVERLAY, ON BOTH SIDES.
       *
       * 🚨 **ALEX, 2026-08-18:** *"the bot turn and player turn is not a clear separation"* and
       * *"please make the bot round stop cleanly, then the player round, then stop cleanly, then the
       * bot round etc."*
       *
       * ⛔ **IT ONLY EVER ANNOUNCED OUR OWN TURN.** So the boundary a player could see was
       * one-sided: their turn was declared, the bot's simply began. With the bot's whole turn
       * arriving as a run of paced events, that meant the board started moving with nothing having
       * said whose move it was - which is precisely "not a clear separation". Announcing BOTH makes
       * the round a thing with a start, and the server's `SettleMs` pause gives it an end. */
      const turnNo = model.timer.Turn ?? 0;

      /* 🚨 **"IT SAID YOUR TURN BUT THE BOT WAS ATTACKING YOU."** Alex, 2026-08-18, and this is the
         same complaint as the very first one in the batch: *"your turn is out of sync, your turn is
         actually the start of the bot's turn."*
         
         ⛔ **THE SERVER ORDERING IS CORRECT AND THE CLIENT UNDOES IT.** `ReleaseHeldTurnChangeAsync`
         deliberately holds the turn-change until AFTER the bot's paced actions have been sent - that
         was built for exactly this. But the CLIENT buffers those actions in `model.visuals` and
         drains them in `playVisuals()` AFTER the render, while the banner fires DURING it. So the
         events arrive in the right order and are PRESENTED in the wrong one: YOUR TURN flashes, then
         the bot's blows animate underneath it.
         
         ⭐ **THE BANNER WAITS FOR THE BOARD TO FINISH SPEAKING.** While anything is still queued to
         animate, the hand-off is deferred to a later render - `announcedTurn` is not advanced, so it
         fires on the first pass where the board is quiet. A hand-off drawn over motion is not a
         boundary, which is the whole reason it kept not landing. */
      // Did OUR turn just end, and did we end it? Checked before the banner, so the warning and the
      // hand-off do not fight for the same moment.
      if (model.timer.State === "Game" && turnNo !== watchedTurn) {
        if (watchedTurn >= 0 && !model.ourTurn() && !endedByChoice && wasOurTurn) {
          missedTurns += 1;
          const left = Math.max(0, AFK_LIMIT - missedTurns);
          announce(el, left > 0
            ? `TURN MISSED · ${left} MORE AND YOU FORFEIT`
            : "TURN MISSED · YOU FORFEIT");
        }
        watchedTurn = turnNo;
        wasOurTurn = model.ourTurn();
        endedByChoice = false;
      }

      /* ⚠️ **AND NOT WHILE THE BOARD IS STILL SHOWING THE LAST TURN.** `model.visuals` is drained by
         `playVisuals()` AFTER this render, so announcing here would put the hand-off ON TOP of the
         bot's blows - which is exactly what Alex saw. `announcedTurn` is left un-advanced, so the
         banner fires on the first render where the queue is empty. */
      if (turnNo !== announcedTurn && model.timer.State !== "Mulligan" && !model.ended
          && model.visuals.length === 0) {
        announcedTurn = turnNo;
        if (model.ourTurn()) {
          announce(el, "YOUR TURN", "ours");
          // ⭐ The turn becoming OURS is the single most important thing to notice on this board.
          // ⚠️ Deliberately NOT played for the opponent's banner: a "your turn" chime on their turn
          // is worse than silence, because it trains the player to act on the wrong cue.
          audio.play("yourTurn");
          audio.play("cardInfo", 0.9);
        } else {
          const them = model.players.find((p) => p.UserId !== model.selfId);
          // ⚠️ Their NAME, not "opponent". The log uses the same name, so the banner and the record
          // agree about who did what.
          announce(el, `${(them?.UserName ?? "OPPONENT").toUpperCase()}'S TURN`, "theirs");
        }
      }

      const unhandled = Array.from(model.unhandled.entries())
        .map(([k, v]) => `${k} x${v}`)
        .join(", ");

      // 🚨 **DEVELOPER TELEMETRY IS NOT PLAYER UI.** This line printed the turn, the phase AND the
      // list of engine events the client cannot yet apply - across the top of the board, clipped at
      // both ends. Alex, 2026-08-14: *"What is at the top? it is cut off."* It was mine, and it had
      // no business on a player's screen.
      //
      // ⭐ The player gets what a player needs: whose turn it is. Everything else moves behind
      // `?matchdump=1`, where it is still one glance away for me and invisible to them.
      // ⭐ **A STABLE CONTRACT FOR THE HARNESS, SEPARATE FROM THE PRESENTATION.** `verify_web_match`
      // used to read the phase out of a visible status line; renaming that line for the player broke
      // the gate, which then reported a game defect that did not exist. State a machine reads should
      // never be scraped from words a human reads - they change for different reasons.
      el.dataset["phase"] = phase;
      el.dataset["turn"] = String(model.timer.Turn ?? 0);
      el.dataset["ourturn"] = String(model.ourTurn());
      // ⭐ **EVERY NUMBER A GATE NEEDS, PUBLISHED ONCE.** Three separate harness breakages came from
      // scraping presentation: the status line was renamed, `.board__who` was replaced by the
      // shield, and a target class changed. Each time the gate reported a GAME defect that did not
      // exist. State a machine reads and words a human reads change for different reasons and must
      // not share a channel.
      el.dataset["theirhp"] = String(stat(model.hero(them?.UserId)?.Hp));
      // ⭐ **ARMOR IS PART OF EFFECTIVE HEALTH AND MUST BE ON THE RAIL SEPARATELY.** The engine spends
      // armor before health, so a gate reading `theirhp` alone is measuring a number the game does
      // not decide anything by - and one that did exactly that passed a puzzle whose authored line
      // dealt 7 into an effective 9. Published, not derived, so nothing has to know the rule.
      el.dataset["theirarmor"] = String(stat(model.hero(them?.UserId)?.Armor));
      el.dataset["ourarmor"] = String(stat(model.hero(me?.UserId)?.Armor));
      el.dataset["ourhp"] = String(stat(model.hero(me?.UserId)?.Hp));
      el.dataset["ourfoxfire"] = String(stat(me?.Mana));
      el.dataset["ourhand"] = String(model.zone(me?.UserId, "InHand").length);
      el.dataset["ourtable"] = String(model.zone(me?.UserId, "InTable").length);
      el.dataset["theirtable"] = String(model.zone(them?.UserId, "InTable").length);

      // ⭐ ONE OWNER FOR "what is happening / what do I do". The old line said "OPPONENT'S TURN"
      // during the mulligan, which is not merely unhelpful - it is nobody's turn during a mulligan.
      const g = guidance(model, swapping.size);
      // ⭐ An armed power OVERRIDES the phase line: the player is mid-action, and the rail is the
      // one place that says what the next click will do.
      // ⭐ **AIMING OVERRIDES THE PHASE LINE, for the same reason an armed power does**: the player
      // is mid-action and the rail is the one place that says what the next click will do.
      //
      // ⚠️ Set HERE rather than with `say()` at arm time. `render` recomputes the rail from
      // `guidance` every pass, so a message written alongside the arming survives until the next
      // render and no longer - the gate caught the rail still reading "YOUR TURN" while the board
      // was waiting for a target.
      if (aiming) {
        /* ⭐ **NAME THE CARD.** "Tap a highlighted card for this to hit" does not say WHAT is
           waiting, so a player who clicked something a second ago has no idea which card is in
           limbo - and the card is out of their hand while they work it out. Alex: *"no idea what
           happened"*. */
        const held = cardName(model.objects.get(aiming.objectId)?.DataId) || "That card";
        g.stage = "CHOOSE A TARGET";
        g.action = `${held} is waiting for a target. Tap a highlighted card, or press CANCEL to take it back.`;
        g.waiting = false;
      } else if (powerArmed) {
        g.stage = "CHOOSE A TARGET";
        g.action = "Tap any creature or hero to use your hero power. Press ESC to cancel.";
        g.waiting = false;
      }
      say(g.stage, g.action);
      status.classList.toggle("match__turnstate--ours", model.ourTurn());
      status.classList.toggle("match__turnstate--waiting", g.waiting);
      // The harness reads these, never the words - see the data-* contract note above.
      // ⭐ The countdown, mounted against the action plate rather than out on the rail.
      el.querySelector(".clock")?.remove();
      el.append(clockEl(model.timer, model.ourTurn()));
      el.dataset["stage"] = g.stage;
      el.dataset["instruction"] = g.action;

      // ⭐ **APPLIED AT THE END OF EVERY RENDER, so the highlight survives one.** Setting it before a
      // render is decoration on a corpse - see `markAimable` for what that cost.
      markAimable();

      if (dump) {
        debugLine.textContent =
          `turn ${model.timer.Turn ?? 0} · round ${model.timer.Round ?? 0} · ${phase || "-"}` +
          (unhandled ? ` · not yet applied: ${unhandled}` : "");
      }
    }

    /**
     * End the mulligan - with or without replacements.
     *
     * 🚨 **THE COMMAND DEPENDS ON WHETHER ANYTHING IS BEING SWAPPED, AND SENDING THE WRONG ONE HANGS
     * THE MATCH SILENTLY.** From `MatchSession.ConfirmHumanMulliganIfRequested`, which carries the
     * measurement in its own comment:
     *
     *     PerformPhaseCmd    <- DONE with no replacement
     *     PerformEffectCmd   <- a replacement resolving
     *
     * and either one means "I am done choosing". `ReadyToPlayCmd` - which this screen sent first -
     * only sets `IsReady` and logs *"I'm ready now"*, so the timer sits in Mulligan with no turn
     * owner forever while the screen says WAITING FOR OPPONENT. Measured here before the fix:
     * pressing DONE moved `ChangePlayerState` from 5 to 11 and changed nothing else.
     *
     * ⚠️ The EXECUTOR is our HERO, because the Mulligan effect is applied to the hero
     * (`ReadyToMulliganCmd` does `CreateAppliedEffectAuto(Mulligan, hero)`), and the TARGETS are the
     * cards being thrown back.
     */
    function confirmMulligan(): void {
      const hero = model.hero(model.selfId);

      // ⭐ **ONE COMMAND FOR BOTH CASES. THE TARGETS ARE THE ONLY DIFFERENCE.**
      // `PerformPhaseCmd` builds a `PhaseInfo(phase, initiator, executor, damageType, TARGETS)` and
      // runs the APPLIED effects for that phase (`Revival.Core.decompiled.cs:18006`), which reaches
      // `MulliganEffect.OnExecute` -> `ReplaceMulliganCards(owner, targets)` (`:14595`). An empty
      // target list replaces nothing and simply passes the mulligan, which is exactly "DONE".
      //
      // ⛔ **DO NOT USE `PerformEffectCmd` HERE. IT CREATES A SECOND MULLIGAN.** That command calls
      // `CreateAndExecuteEffect`, and `MulliganEffect.Create()` deals a fresh starting hand - so it
      // dealt FIVE MORE cards instead of swapping two. Measured on the server, which is the only
      // place it was visible: `choose 5 -> 10, deck 25 -> 20`. On screen the mulligan simply ended
      // and the match looked fine.
      //
      // ⚠️ `InChooseAccepted` is the phase for "the player has accepted their choose zone"
      // (`EffectPhase`, `:21812`). The cards are `InChoose`; accepting them is this phase.
      send("PerformPhaseCmd", {
        ExecutorObjectId: hero?.Id ?? 0,
        TargetObjectsIds: Array.from(swapping),
        MetaJson: JSON.stringify({
          Phase: "InChooseAccepted",
          Initiator: hero?.Id ?? 0,
        }),
      });
      swapping.clear();
    }

    function send(cmd: string, extra: Record<string, unknown> = {}): void {
      // ⚠️ ECHO THE TIMER'S CURRENT HASH. We never compute it - see the class note.
      void hub
        ?.performCommand(cmd, { TimeHash: model.timer.TimeHash ?? 0, ...extra })
        .catch((e) => {
          say(stageLine.textContent ?? "", `That move was refused: ${describe(e)}`, true);
          status.classList.add("page__note--error");
        });
    }

    /**
     * THE FOXFIRE RAIL. The typed pool a player has, and what it can pay for.
     *
     * ⚠️ Rendered from the SERVER's pool, never accumulated client-side. A second tally of a
     * resource is a second owner, and this one decides whether a card can be played.
     */
    function foxfireHud(): HTMLElement {
      const rail = document.createElement("div");
      rail.className = "foxfire";

      if (!foxfire.enabled) {
        // ⚠️ Say so. `REVIVAL_TYPED_FOXFIRE=0` is a ROLLBACK, not a mode - if it is off, that is
        // worth seeing rather than silently reverting to the original's rules.
        rail.classList.add("foxfire--off");
        rail.textContent = "typed Foxfire is OFF";
        return rail;
      }

      const label = document.createElement("span");
      label.className = "foxfire__label";
      label.textContent = "FOXFIRE";
      rail.append(label);

      const pool = foxfire.pool ?? {};
      // ⚠️ **NEUTRAL IS NOT A SPENDABLE COLOUR AND MUST NOT BE DRAWN AS ONE.** No card requires it,
      // the server no longer mints it, and a match started before that change can still be carrying
      // some. Drawing "NEUTRAL 2" in a resource rail is the exact thing that made the mechanic read
      // as unpredictable.
      const colours = Object.keys(pool).filter((c) => hasRealm(c) && (pool[c] ?? 0) > 0);
      // 🔴 **THE RAIL MUST TEACH THE RULE, NOT JUST COUNT TOKENS.** Alex, 2026-08-17: *"the mechanic
      // of burning x card to replenish foxfire for x colour is not clear even to me."* The empty
      // state said "burn a card to feed it", which is true and omits the only part that is hard:
      // WHICH card. You do not get a token, you get a token of THAT CARD'S realm.
      const rule = document.createElement("span");
      rule.className = "foxfire__rule";
      rule.textContent = "burn a card to gain 1 token of ITS realm";
      rail.append(rule);

      if (colours.length === 0) {
        const empty = document.createElement("span");
        empty.className = "board__empty";
        empty.textContent = "no tokens yet";
        rail.append(empty);
        return rail;
      }

      for (const colour of colours) {
        const n = pool[colour] ?? 0;
        const token = document.createElement("span");
        token.className = "token";
        // ⭐ **THE COLOUR IS THE CARD'S `quadrant`** - the four realms plus Neutral, and the pip is
        // drawn IN that realm's colour. Rendering it as the WORD "NEUTRAL 1" made a colour mechanic
        // colourless, which is the one way it cannot work.
        token.style.setProperty("--realm", realmColour(colour));
        token.title = `${n} ${realmLabel(colour)} token${n === 1 ? "" : "s"}. `
                    + `Spent on ${realmLabel(colour)} cards. Burn a ${realmLabel(colour)} card for another.`;

        for (let i = 0; i < n; i++) {
          const p = document.createElement("i");
          p.className = "token__pip";
          token.append(p);
        }
        const label = document.createElement("b");
        label.className = "token__label";
        label.textContent = `${realmLabel(colour)} ${n}`;
        token.append(label);
        rail.append(token);
      }
      return rail;
    }

    /**
     * A realm's name as a player reads it, in the SAME short form the card face uses.
     *
     * ⚠️ MUST MATCH `realmShortName` in match/card.ts. A token labelled "Domain of Negafook" beside
     * a card labelled "NEGAFOOK" reads as two different things, and the whole point of the label is
     * that a player can match one to the other without translating.
     */
    function realmLabel(q: string): string {
      return q.replace(/^The_/, "").replace(/^[A-Za-z]+_of_/, "").split("_")[0] ?? q;
    }

    /**
     * A realm's colour, from the ONE owner.
     *
     * 🚨 **THIS FUNCTION USED TO CARRY A HAND-WRITTEN MAP, UNDER A COMMENT SAYING IT DID NOT.** The
     * old note read *"RevivalTheme decides these, not this file"* - true of the COLOURS and false of
     * the PAIRING, which sat right here. That half-truth is exactly what stops anyone checking
     * (`reference_diverge_by_design_is_a_claim`). `verify_open_sets.py` found it in one run.
     *
     * ⭐ Now generated: `RevivalTheme.cs` states the quadrant on the same line as the colour
     * (`// quadrant:The_Spirit_Veil`) and `stage_card_frames.py` emits the map. Adding a realm is
     * ONE line there - which matters, because Alex is expanding the realms and minting the cards.
     *
     * ⛔ **AN UNKNOWN REALM IS NOT PAINTED NEUTRAL.** It returns the muted ink instead, so it reads
     * as unrecognised. Dressing it in Neutral's colour would misreport what pays for the card, and
     * look entirely deliberate doing it.
     */
    function realmColour(q: string): string {
      return REALM_COLOUR[q] ?? Theme.Ink;
    }

    /** Open a graveyard so its cards can be read. Public information, shown in full. */
    function openGraveyard(userId: string | undefined, title: string): void {
      el.append(
        graveyardOverlay(title, model.zone(userId, "InDiscard"), (c) => cardEl(c)),
      );
    }

    /**
     * Play the visual events the server just sent.
     *
     * ⭐ **THE SERVER SAYS WHAT HAPPENED AND TO WHOM; WE ONLY DRAW IT.** `ObjectHitEvent` carries the
     * target and the stat's From/To, so the damage number is the engine's arithmetic, not ours. A
     * client that computed damage to animate would be a second, quietly-diverging rules engine.
     *
     * ⚠️ **A DEATH IS PLAYED BEFORE THE OBJECT LEAVES THE BOARD.** The card element still exists on
     * this render because the state update and the render happen together; by the next render it is
     * gone. Animating on the following frame would find nothing.
     */
    function playVisuals(): void {
      // ⭐ RE-APPLY ANYTHING STILL IN FLIGHT. The render just replaced the elements these were on.
      const now = performance.now();
      /* ⚠️ **THIS WINDOW MUST OUTLAST THE LONGEST MARK OR THE MARK IS CUT SHORT BY A RENDER.**
         It was 700ms while `strike-mark` ran 760ms and `hit-mark` 700ms - so a render landing
         mid-animation dropped the class and the cue vanished early, which is a second, quieter cause
         of the same "I cannot tell what attacked" report. The marks now hold 1400ms, so this holds
         longer than that. One number, derived from the animations rather than guessed alongside
         them. */
      const MARK_MS = 1500;
      for (const [id, a] of inFlight) {
        if (now - a.at > MARK_MS) {
          inFlight.delete(id);
          continue;
        }
        const node = el.querySelector<HTMLElement>(`[data-obj="${id}"]`);
        if (node && !node.classList.contains(a.cls)) node.classList.add(a.cls);
      }

      // ⭐ **WHAT THE OTHER PLAYER SAID.** Drained here rather than rendered by the model: the model
      // owns state, the screen owns pixels.
      for (const e of model.drainEmotes()) {
        const def = emoteByExpr(e.emotionId);
        if (!def) continue;
        const target = el.querySelector<HTMLElement>(`[data-hero-owner="${e.senderId}"]`);
        if (target) showEmote(target, def);
      }

      // ⚠️ **STAGGERED - AND THE STEP IS THE ANIMATION'S OWN LENGTH WHEN IT IS THE OPPONENT ACTING.**
      //
      // 🚨 This was a flat 55ms, chosen as "above the threshold where the eye merges them". That is
      // the wrong threshold. The spawn animation is 380ms and a strike is 420ms, so a 55ms step
      // starts the next card while the previous one is still 15% of the way through its motion.
      // Seven of them overlap into a single smear - which is precisely the reported defect:
      // *"AI is too fast, it does all the action at once, so it is hard to follow what he does."*
      //
      // ⭐ **THE ORIGINAL NEVER HAD A STAGGER CONSTANT AT ALL.** `Revival.Client.decompiled.cs:59325`
      // reads logic events off a channel with `SubscribeAwait(ProcessUnQueueAsync)` - each event's
      // whole visual sequence completes before the next is dequeued. Its pacing IS the animation
      // length, serialised. Alex: *"the original is a much better UX"*, *"it feels like you are
      // playing a human."* Matching that here means stepping by the motion, not by a number picked
      // to feel snappy.
      //
      // ⚠️ **OUR OWN ACTIONS KEEP THE TIGHT STEP.** A paced response to your own click is lag, and
      // several of our own events in one batch (play, then the rally it triggers) genuinely are one
      // action. Only what the OPPONENT does needs to be legible one thing at a time.
      // 🚨 **DERIVED PER EVENT FROM WHO OWNS THE OBJECT - NOT FROM `ourTurn()`.**
      //
      // `!model.ourTurn()` was the obvious test and it would NEVER FIRE: the server only pushes the
      // odd turns, so `ourturn` reads true for the entire match and the client believes it is always
      // its own move. That is the same defect that made the server-side guard dead code, repeated one
      // layer up - **a flag read from the clock, when the clock is the thing that is broken.**
      // Measured by `tools/verify_ai_pacing.py`, which saw zero opponent actions because its own
      // trigger depended on the same lie.
      //
      // Ownership of the affected object cannot lie: a creature on their side of the table is theirs
      // whatever the timer claims.
      const isTheirs = (id: number | undefined): boolean => {
        if (typeof id !== "number") return false;
        const o = model.objects.get(id);
        return !!o && o.OwnerUserId !== undefined && o.OwnerUserId !== model.selfId;
      };
      // Spawn 380ms, strike 420ms, plus a beat to read the result before the next begins.
      const STEP_THEIRS = 620;
      const STEP_OURS = 55;
      let stagger = 0;
      for (const v of model.drainVisuals()) {
        // ⭐ **SAY WHAT THE OPPONENT JUST DID, IN WORDS.** Testers, 2026-08-15: *"AI is too fast, it
        // does all the action at once, so it is hard to follow what he does, and learn from it ...
        // would start adding in the tooltip and have AI moves visible to follow."*
        //
        // Slowing the bot (the server paces a beat per action now) buys TIME to watch; this says
        // WHAT was watched. Animation alone is ambiguous - a card fading could be a death, a
        // discard, or a return to hand, and a new player cannot tell which.
        //
        // ⚠️ Only when it is NOT our turn: narrating our own clicks back to us is noise.
        /* ⭐ **BOTH SIDES NOW.** It used to narrate the opponent only, on the reasoning that
           "narrating our own clicks back to us is noise". That is true of a FLASH MESSAGE and false
           of a RECORD: Alex, 2026-08-18, asked for the log to *"separate with the players names"*,
           which is not a thing a one-sided log can do. */
        noteAction(v);
        const node = el.querySelector<HTMLElement>(`[data-obj="${v.id}"]`);
        if (!node) continue;

        // Each event in the batch starts a beat after the last one.
        // A blow landing on OUR card is still the opponent acting - `v.by` is the striker.
        const theirs = isTheirs(v.id) || isTheirs(v.by);
        node.style.setProperty("animation-delay", stagger > 0 ? `${stagger}ms` : "");
        stagger += theirs ? STEP_THEIRS : STEP_OURS;

        if (v.kind === "hit") {
          audio.play("damage", 0.8);

          // ⭐ **THE OPPONENT'S ATTACKS ARE NOW VISIBLE TOO.** `ObjectHitEvent` does not name the
          // attacker, so the board could only animate OUR swings - the bot's attacks were a number
          // and a dying card, and Alex read that as "my cards just disappear". The model pairs the
          // hit with whoever spent a move in the same batch (`PerformAttackCmd` ends with
          // `SpendMove`), which is enough to draw the blow from the right place.
          if (typeof v.by === "number") {
            const fromEl = el.querySelector(`[data-obj="${v.by}"]`);
            const stage = el.closest<HTMLElement>(".stage");
            if (stage && fromEl) fireArrow(stage, fromEl, node);
            strike(v.by);
            audio.play("attack", 0.85);
          }
          // ⚠️ A SHIELD IS NOT A CARD. It is bigger and anchored, so it takes a heavier, shorter
          // shake - the same motion on both reads as a card-sized wobble on a large object.
          // ⭐ **CONSEQUENCE.** A blow on a HERO shakes the board itself: the hero is the thing the
          // whole match is about, and a hit on it should be felt, not just seen. A creature trading
          // blows does not shake the world - reserving it is what keeps it meaningful.
          const isHero = node.classList.contains("shield");
          if (isHero) {
            board.classList.remove("board--shaken");
            void board.offsetWidth;
            board.classList.add("board--shaken");
            window.setTimeout(() => board.classList.remove("board--shaken"), 320);
          }
          const cls = isHero ? "shield--hit" : "card--hit";
          inFlight.set(v.id, { cls, at: now });
          node.classList.remove(cls);
          // Force a reflow so the class re-applies when the same target is hit twice in a row.
          void node.offsetWidth;
          node.classList.add(cls);
          if (v.amount && v.amount > 0) {
            const dmg = document.createElement("span");
            dmg.className = "damage";
            dmg.textContent = `-${v.amount}`;
            node.append(dmg);
            dmg.addEventListener("animationend", () => dmg.remove());
          }
        } else if (v.kind === "death") {
          audio.play("death", 0.9);
          inFlight.set(v.id, { cls: "card--dying", at: now });
          node.classList.add("card--dying");
        } else if (v.kind === "spawn") {
          audio.play("cardSpawn", 0.7);
          inFlight.set(v.id, { cls: "card--spawning", at: now });
          node.classList.remove("card--spawning");
          void node.offsetWidth;
          node.classList.add("card--spawning");
        }
      }
    }

    /**
     * The targets the ENGINE would accept for the selected attacker, recomputed per render.
     *
     * ⚠️ Not cached across renders: taunt can be granted or silenced mid-turn by another card, and
     * a cached legal-target set is a rule that stops being true without telling anyone.
     */
    function legalTargets(): Set<number> {
      if (attacker === null) return new Set();
      return legalAttackTargets(model, attacker, model.opponent()?.UserId);
    }

    /**
     * ⚠️ **A MODE YOU CANNOT LEAVE IS A TRAP.** Arming the power changes what every click on the
     * board does, so there has to be a way out that is not "use it on something you did not want".
     */
    const onBoardKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && powerArmed) {
        powerArmed = false;
        render();
      }
    };
    window.addEventListener("keydown", onBoardKey);
    cancelBoardKeys = () => window.removeEventListener("keydown", onBoardKey);

    /**
     * Fire the armed hero power at `targetId`.
     *
     * 🚨 **`PerformPhaseCmd` WAS THE WRONG ROUTE, AND IT FAILED SILENTLY.** Sending the
     * `AbilityButtonPress` phase was accepted by the server, logged, and resolved NOTHING - no
     * foxfire spent, no heal. The phase is what the ORIGINAL CLIENT runs locally through its own
     * `EffectHandler`; on the wire the engine wants the effect named:
     *
     *     PerformEffectCmd { EffectDataId, RuntimeArgs }  + Executor + Targets
     *     (Revival.Core :17987 - it throws "Empty effectConfigId passed!" without the id)
     *
     * ⭐ **AND THE ID IS IN THE DATA**: a hero's `effectsIds[0]` is its power
     * (`hero-power-asclepius-restorative-touch`). It is an ENGINE IDENTIFIER, which is why it still
     * reads Greek and must never be renamed - rename the LABEL, never the KEY.
     */
    function useHeroPower(targetId: number): void {
      const ourHero = model.hero(model.self()?.UserId);
      powerArmed = false;
      powerUsedOnTurn = Number(model.timer.Turn ?? 0);
      audio.play("heroPower");
      const stage = el.closest<HTMLElement>(".stage");
      const fromEl = el.querySelector(`[data-obj="${ourHero?.Id ?? 0}"]`);
      const toEl = el.querySelector(`[data-obj="${targetId}"]`);
      if (stage && fromEl && toEl) fireArrow(stage, fromEl, toEl);
      const effectId = cardRecord(ourHero?.DataId)?.effectsIds?.[0];
      if (!effectId) {
        // ⛔ Never send a command we know the engine will throw on. Say why instead.
        say(stageLine.textContent ?? "", "This hero has no power to use.", true);
        render();
        return;
      }
      send("PerformEffectCmd", {
        ExecutorObjectId: ourHero?.Id ?? 0,
        TargetObjectsIds: [targetId],
        MetaJson: JSON.stringify({ EffectDataId: effectId }),
      });
      render();
    }

    /** The attacker lunges. Played on OUR action, because we know when we sent it. */
    function strike(objectId: number): void {
      // Registered as in-flight FIRST: the command we are about to send triggers a render almost
      // immediately, and without this the lunge would be wiped before a frame of it was drawn.
      inFlight.set(objectId, { cls: "card--striking", at: performance.now() });
      const node = el.querySelector<HTMLElement>(`[data-obj="${objectId}"]`);
      if (!node) return;
      node.classList.remove("card--striking");
      void node.offsetWidth;
      node.classList.add("card--striking");
    }

    function centre(): HTMLElement {
      const d = document.createElement("div");
      d.className = "board__centre";
      return d;
    }

    /**
     * One player's half of the board: their HUD strip, then their row of seven.
     *
     * ⚠️ The player and their hero are DERIVED from the model here rather than passed in. They were
     * arguments, and the caller had to look both up correctly for each side - two call sites, two
     * chances to hand the opponent's hero to your own row.
     */
    function side(table: RuntimeObject[], isOpponent: boolean): HTMLElement {
      const row = document.createElement("section");
      row.className = isOpponent ? "board__side board__side--them" : "board__side";

      const player = isOpponent ? model.opponent() : model.self();

      // ⭐ **THE ORIGINAL'S BAR, COPIED** (`art/cdn/boardlayout.png`): name plate on the left, the
      // hero SHIELD dead centre on the edge, the foxfire rail on the right. Deck, graveyard and the
      // timer live on the side rails, where the original puts them.
      const head = document.createElement("div");
      head.className = "board__bar";

      const hero = model.hero(player?.UserId);
      // ⭐ **THE HERO IS NOT ALWAYS A LEGAL TARGET, AND THAT IS THE WHOLE OF "CARDS PROTECT THE
      // SHIELD".** While a taunting creature stands, the engine's `CheckTaunt` refuses an attack on
      // the hero - so offering it produced a click that did nothing. See `match/rules.ts`.
      const canTargetHero = isOpponent && attacker !== null && hero?.Id !== undefined
        && legalTargets().has(hero.Id);
      // ⚠️ A hero is a unit for the power's purposes, and healing YOUR OWN hero is the commonest use.
      const heroIsPowerTarget = powerArmed && hero?.Id !== undefined;

      const nameplate = document.createElement("div");
      nameplate.className = "nameplate";
      // ⚠️ textContent. Another player's name is untrusted text.
      nameplate.textContent = player?.UserName ?? (isOpponent ? "OPPONENT" : "YOU");
      if (player?.IsBot) nameplate.dataset["bot"] = "BOT";

      // ⭐ THE HERO POWER, on OUR hero only - see `heroPowerEl` for the original's conditions.
      const powerRec = isOpponent ? undefined : cardRecord(hero?.DataId);
      const powerName = String(powerRec?.heroPowerName ?? "");
      const powerCost = Number(powerRec?.heroPowerCost ?? 0);
      const turnNow = Number(model.timer.Turn ?? 0);
      // ⚠️ `player` IS the side being drawn; inside this function that is the owner of this
      // half of the board, so the foxfire read must come from it rather than an outer `me`.
      const affordable = stat(player?.Mana) >= powerCost;
      const usedThisTurn = powerUsedOnTurn === turnNow;
      const powerReady = !isOpponent && !!powerName && model.ourTurn() && affordable
        && !usedThisTurn && !model.ended;
      const powerWhy = !model.ourTurn() ? "Not your turn"
        : usedThisTurn ? "Already used this turn"
        : !affordable ? `Needs ${powerCost} foxfire`
        : "";

      /*
       * 🚨 **A PROTECTED HERO LOOKED IDENTICAL TO AN UNREACHABLE ONE.** Alex relayed this from a
       * player, 2026-08-18: *"I am attacking the shield, but it is not taking damage, I think
       * because a card is defending it, but as a player you do not see or understand that is
       * happening."*
       *
       * The rule was working perfectly. `CheckTaunt` refuses the attack, so the hero was simply not
       * offered as a target - and **an affordance that silently disappears reads as a broken game,
       * while one that shows WHY reads as a rule to play around.** The creatures already had this:
       * `.card--guarded` draws a protected creature hatched rather than just leaving it unclickable.
       * The hero, the thing a player is actually trying to kill, did not.
       *
       * ⭐ So while an attacker is chosen and the hero is NOT legal because something is taunting,
       * the shield is drawn guarded and says so in its own tip.
       */
      /* 🔴 **IT ONLY SAID SO WHILE AN ATTACKER WAS SELECTED, AND ONLY IN A TOOLTIP.** Alex hit the
         very same wall the note above was written for, one day later: *"I have attacked the opposing
         shield many times but it is not taking any damage."* Belsnickel, Who Mocks was taunting on
         the bot's side the whole time.
         
         ⛔ Two reasons the fix did not reach him. It required the player to have ALREADY chosen an
         attacker - so the board looked identical before the decision, which is when the plan is
         made. And it spoke through a `title`, which moved to right-click the same day. **An
         explanation that needs the player to already suspect the rule cannot teach the rule.**
         
         ⭐ So a defended hero is now drawn defended WHENEVER a taunter stands, and it says the
         defender's name in text on the board. */
      const taunters = isOpponent
        ? model.zone(player?.UserId, "InTable").filter((c) => hasKeyword(c, "Taunting"))
        : [];
      const heroBlocked = isOpponent && taunters.length > 0;

      const shield = heroEl(hero, player, heroIsPowerTarget
        ? { targetable: true, onTarget: () => useHeroPower(hero!.Id as number) }
        : canTargetHero
        ? { targetable: true, onTarget: () => attack(hero!.Id as number) }
        : {});

      /* 🚨 **"IF IT CANNOT BE ATTACKED THEN IT SHOULD NOT BE AN OPTION."** Alex, 2026-08-18, after
         hitting this twice: *"i am attacking the opponent shield but it takes no damage... Why are
         you ignoring these things?"*
         
         He is right about the principle and the board was already half-doing it: with a taunter up
         the hero is genuinely NOT targetable, so it has no click handler at all. **But a control
         that silently does nothing is indistinguishable from one that is broken**, which is exactly
         what he described. Removing the affordance is not enough; the click has to be ANSWERED.
         
         ⚠️ Attached whenever a taunter stands, not only mid-attack, for the same reason the badge is:
         the player forms the plan before selecting an attacker. */
      /* ⚠️ **AND WITH NO ATTACKER CHOSEN, CLICKING THE ENEMY HERO ALSO DID NOTHING.** Found by
         playing rather than reading - the playthrough log says it plainly: *"CLICKING THE ENEMY HERO
         CHANGED NOTHING AND SAID NOTHING."* Tapping the thing you are trying to kill is the most
         natural first move in the game, and it was the one gesture with no response at all. */
      if (isOpponent && attacker === null && !heroIsPowerTarget && model.ourTurn()) {
        shield.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          say(stageLine.textContent ?? "",
              "Tap one of YOUR creatures first, then tap what it should hit.", true);
        });
      }

      if (isOpponent && taunters.length > 0 && !canTargetHero) {
        shield.style.cursor = "not-allowed";
        shield.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const names = taunters
            .map((c) => cardName(String(c.DataId)) || "a defender")
            .filter((n, i, a) => a.indexOf(n) === i);
          say(stageLine.textContent ?? "",
              names.length === 1
                ? `${names[0]} is defending. It has to fall before the hero can be hit.`
                : `${names.join(" and ")} are defending. One of them has to fall first.`,
              true);
        });
      }

      if (heroBlocked) {
        shield.classList.add("board__who--guarded");
        // ⭐ **ON THE BOARD, NOT IN A TOOLTIP.** The one fact that explains why the hero cannot be
        // hit has to be readable without hovering, right-clicking or guessing.
        const badge = document.createElement("span");
        badge.className = "board__guardnote";
        const first = cardName(String(taunters[0]?.DataId ?? "")) || "a defender";
        badge.textContent = taunters.length === 1
          ? `PROTECTED BY ${first.toUpperCase()}`
          : `PROTECTED BY ${taunters.length} DEFENDERS`;
        shield.append(badge);
        // ⚠️ NAMES the defender rather than saying "blocked". A player who knows WHICH creature is
        // in the way knows what to kill first, which is the decision the rule exists to create.
        const names = taunters
          .map((c) => cardName(String(c.DataId)) || "a defender")
          .filter((n, i, a) => a.indexOf(n) === i);
        shield.setAttribute(
          "title",
          names.length === 1
            ? `${names[0]} is defending. Kill it first, or attack it instead.`
            : `${names.join(" and ")} are defending. One of them has to fall first.`,
        );
      }

      // ⭐ **THE EMOTE WHEEL LIVES ON YOUR OWN SHIELD, AS IT DID IN THE ORIGINAL.** Alex: *"in the
      // original game if you clicked on the shield a number of reaper animations came up."* The
      // shield is the only object on the board that IS you rather than something you own, so it is
      // where a player already looks to find themselves.
      //
      // ⚠️ Ours only. Clicking THEIR shield means attack, and an emote wheel there would eat a
      // lethal swing.
      if (!isOpponent) attachEmoteWheel(shield);
      // Tagged so an arriving emote can find the right shield without the model knowing about pixels.
      shield.dataset["heroOwner"] = String(player?.UserId ?? "");

      /*
       * 🚨 **THE HERO POWER WAS A FOURTH CHILD IN A THREE-COLUMN GRID, AND IT LANDED ON THE TRAY.**
       *
       * Alex, 2026-08-18, with a screenshot: *"this 2 Unholy Pact is covered."* Measured at his exact
       * viewport (1866x1115, from his own bug report snapshot): the power sat at x 327-832, y 821-858
       * and the tray's foxfire rail at x 622-1070, y 825-874 - **210px of horizontal overlap and 33
       * of vertical.**
       *
       * ⭐ **THE CAUSE IS `board__bar`'s TEMPLATE**: `minmax(0,1fr) auto minmax(0,1fr)`, three columns,
       * and this appended a FOURTH child. It wrapped to an implicit second row - and `.match` is
       * `height: 100%; overflow: hidden` with fixed rows, so there was no room for that row and it
       * drew over the tray beneath it.
       *
       * ⚠️ **IT ONLY BITES ON A WIDE SCREEN WITH A LONG POWER NAME**, which is why it did not
       * reproduce at the default viewport and why no check caught it. "Restorative Touch" makes the
       * control 506px; a short name does not reach the rail.
       *
       * ⛔ **THE FIX IS NOT TO SHRINK THE LABEL** - that makes the legibility complaint (queue #58)
       * worse. The power now shares column ONE with the nameplate, stacked, so the bar stays exactly
       * one row however long the name is.
       */
      const who = document.createElement("div");
      who.className = "board__whoblock";
      who.append(nameplate);

      head.append(who, shield, foxfirePips(player));

      // ⭐ Inside the nameplate's own column, under the name, so it can never wrap the bar.
      if (!isOpponent && powerName) {
        who.append(heroPowerEl(
          powerName,
          powerCost,
          String(powerRec?.heroPowerDescription ?? ""),
          powerReady,
          powerWhy,
          () => {
            // ⭐ **ARM, DO NOT FIRE.** The power takes a target ("Restore 2 Health. If the target is
            // a unit..."), and the first version sent none - so the engine resolved nothing, no
            // foxfire was spent, and the button silently did nothing. The next click on a creature
            // or a hero is the target.
            powerArmed = true;
            attacker = null;
            audio.play("arrowStart", 0.9);
            render();
          },
        ));
      }

      // ⭐ **SEVEN SLOTS A SIDE, ALWAYS DRAWN.** `maxCardsOnTable` is 7 in the served config, and an
      // EMPTY slot is information: it tells a player how much room they have left and where a card
      // will land. A row that only draws the cards present cannot show either, which is why the
      // first version needed the words "nothing on the table" to say what a board should show by
      // being a board.
      //
      // ⚠️ Read from the config's own number, not typed as a literal in the renderer - see
      // SLOTS_PER_SIDE.
      const tableRow = document.createElement("div");
      tableRow.className = "board__row";
      for (let i = 0; i < SLOTS_PER_SIDE; i++) {
        const slot = document.createElement("div");
        slot.className = "slot";
        const occupant = table[i];
        if (occupant) {
          slot.append(tableCard(occupant, isOpponent));

          /* ⭐ **DRAG A BUFF STRAIGHT ONTO THE CREATURE IT BUFFS.** This is the gesture DayDevil
           * expected, and it is the one that removes the empty-slot requirement instead of routing
           * around it. The slot accepts the drop only when the card in flight actually targets and
           * THIS creature is one of its legal targets, so an ordinary creature drag still falls
           * through to the empty slots and nothing else changes. */
          const occId = occupant.Id ?? -1;
          slot.addEventListener("dragover", (e) => {
            if (!dragging?.aim || !dragging.legal.has(occId)) return;
            e.preventDefault();
            slot.classList.add("slot--aimable");
          });
          slot.addEventListener("dragleave", () => slot.classList.remove("slot--aimable"));
          slot.addEventListener("drop", (e) => {
            if (!dragging?.aim || !dragging.legal.has(occId)) return;
            e.preventDefault();
            slot.classList.remove("slot--aimable");
            const id = Number((e as DragEvent).dataTransfer?.getData("text/plain"));
            if (!Number.isFinite(id) || id <= 0) return;
            audio.play("cardDrop");
            /* ⚠️ **A SPELL DOES NOT OCCUPY A SLOT, SO THE INDEX IS NOT A PLACEMENT.** Send the
             * current row width, which is what the engine treats as "append" - the same value the
             * click fallback uses - and let the TARGET carry the meaning. */
            playCardAt(id, model.zone(model.selfId, "InTable").length, [occId]);
            endDrag();
          });
        } else {
          slot.classList.add("slot--empty");

          // ⭐ **THIS IS HOW A CARD IS PLACED IN THE ORIGINAL: DRAG IT FROM HAND ONTO A SLOT.**
          // Alex, 2026-08-14: *"the board now still doesn't have the mechanic for me to place my
          // cards. How many times do I have to ask you to use the same gameplay and the original
          // game?"* He is right. I had click-to-play, which appended to the end of the row - so the
          // player never chose WHERE, even though `PlayCardCmd` takes `RelativePositionX` for
          // exactly that. The engine always supported placement; the client did not offer it.
          if (!isOpponent && model.ourTurn()) {
            slot.dataset["slot"] = String(i);
            // ⭐ **THE SLOT PULLS THE CARD AND LIGHTS UP.** Alex, 2026-08-14: *"when the player puts
            // a card on the table the spot it is going to should slightly pull it, and the frame
            // should light up, it will feel smoother and more interactive this way."*
            //
            // This is the single most important piece of game feel on the board: it tells the
            // player WHERE the card will land BEFORE they commit, which turns a blind drop into a
            // decision. The pull is a small scale-up on the slot plus a lit frame - the card is not
            // physically moved, because fighting the browser's drag image reads as lag.
            slot.addEventListener("dragenter", () => {
              if (!slot.classList.contains("slot--over")) audio.play("cardHover", 0.6);
            });
            slot.addEventListener("dragover", (e) => {
              e.preventDefault();
              slot.classList.add("slot--over");
            });
            slot.addEventListener("dragleave", () => slot.classList.remove("slot--over"));
            slot.addEventListener("drop", (e) => {
              e.preventDefault();
              slot.classList.remove("slot--over");
              const id = Number((e as DragEvent).dataTransfer?.getData("text/plain"));
              if (Number.isFinite(id) && id > 0) {
                audio.play("cardDrop");
                // ⚠️ **DRAG IS THE REAL MECHANIC, SO IT MUST GO THROUGH THE AIM STEP TOO.** Wiring
                // aiming only into the click fallback would leave the path players actually use
                // still sending an empty target list - the whole defect, surviving in the majority
                // case.
                playWithAim(id, i, model.objects.get(id)?.DataId);
              }
            });
          }
        }
        tableRow.append(slot);
      }

      row.append(head, tableRow);
      return row;
    }

    /**
     * The cards in front of the player right now.
     *
     * ⭐ **DURING THE MULLIGAN THAT IS `InChoose`, NOT `InHand`.** The deal moves the opening five
     * into the choose zone and only commits them to the hand when the mulligan ends. Reading
     * `InHand` throughout is why this first rendered "no cards in hand" against a server that had
     * plainly dealt - deck 30 -> 25 with nothing to show for it.
     */
    function hand(): HTMLElement {
      const wrap = document.createElement("div");
      wrap.className = "board__hand";

      const choosing = model.timer.State === "Mulligan";
      const mine = choosing
        ? model.zone(model.selfId, "InChoose")
        : model.zone(model.selfId, "InHand");

      // ⛔ **THE "YOUR HAND" / "STARTING HAND" CAPTION IS GONE, FOR TWO REASONS.**
      //
      // 1. It was a SECOND OWNER of what the phase rail already says out loud - the rail reads
      //    "OPENING HAND" or "YOUR TURN" at the top of the board, and two labels for one fact drift.
      // 2. It was `position: absolute` with no positioned ancestor, so it resolved against `.match`
      //    and rendered UNDER the hand cards - visible in every capture as a stray letter poking out
      //    from behind the leftmost card.
      //
      // A fan of cards along the bottom edge of a card game does not need to be labelled "hand".

      for (const c of mine) wrap.append(cardChip(c, true));
      if (mine.length === 0) {
        const empty = document.createElement("span");
        empty.className = "board__empty";
        empty.textContent = choosing ? "waiting for the deal" : "no cards in hand";
        wrap.append(empty);
      }
      return wrap;
    }

    /**
     * ⭐ A REAL CARD, not a label. See `match/card.ts` - our art, our mask, our badges.
     * During the mulligan the cards in the choose zone are SELECTABLE, because swapping them is the
     * only thing the mulligan is for.
     */
    /**
     * A card ON THE TABLE. Ours can be chosen as an attacker; theirs can be attacked.
     *
     * ⭐ **SELECT-THEN-TARGET, WHICH IS ALSO HOW THE ORIGINAL PLAYS.** Pick your creature, then pick
     * what it hits. A drag would be closer to the original's feel and is the next step; this is the
     * same two decisions with a click each, so the RULE is complete even though the gesture is not.
     */
    function tableCard(c: RuntimeObject, isOpponent: boolean): HTMLElement {
      const id = c.Id ?? -1;

      /*
       * ⭐ **THE ROLE PLINTH, ON EVERY TABLE CARD ON BOTH SIDES.** Alex, 2026-08-18, asking for
       * zones. It is computed HERE rather than inside `cardEl` because it needs the served card
       * record for the TYPE (Building) as well as the runtime object for the applied effects, and
       * `roleOf` is the one owner of that decision.
       *
       * ⚠️ **BOTH SIDES, DELIBERATELY.** Knowing which of THEIR creatures defends and which merely
       * strikes is the half that changes how you attack; showing it only on your own would answer
       * the easier question.
       */
      const role = roleOf(c, String(cardRecord(c.DataId)?.["type"] ?? ""));

      if (!model.ourTurn()) return cardEl(c, { mini: true, role });

      if (!isOpponent) {
        // ⭐ While the hero power is armed, OUR OWN creatures are legal targets - the power heals
        // and buffs, so restricting targeting to the enemy would hide half of what it does.
        if (powerArmed) {
          return cardEl(c, {
            mini: true,
            role,
            selectable: true,
            playable: true,
            onToggle: () => useHeroPower(id),
          });
        }
        const canAttack = stat(c.MoveCount) > 0;
        return cardEl(c, {
          mini: true,
          role,
          selectable: true,
          selected: attacker === id,
          playable: canAttack,
          onToggle: () => {
            attacker = attacker === id ? null : id;
            // ⭐ `Arrow_Start` when the bow is drawn, `Arrow_Select` when it is loosed. Two clips,
            // two moments - the set names them separately for a reason.
            if (attacker !== null) audio.play("arrowStart", 0.8);
            render();
          },
        });
      }

      if (powerArmed) {
        return cardEl(c, {
          mini: true,
          role,
          selectable: true,
          playable: true,
          onToggle: () => useHeroPower(id),
        });
      }

      // Theirs: a legal target only while one of ours is selected AND the rules allow this target.
      // ⚠️ **A CREATURE THE TAUNT RULE PROTECTS IS DRAWN GUARDED, NOT JUST LEFT UNCLICKABLE.** An
      // affordance that silently disappears reads as a bug; one that shows WHY reads as a rule.
      if (attacker === null) return cardEl(c, { mini: true, role });
      const legal = legalTargets().has(id);
      return cardEl(c, {
        mini: true,
        role,
        selectable: legal,
        playable: legal,
        guarded: !legal,
        // ⚠️ Omitted rather than passed as `undefined`: `exactOptionalPropertyTypes` treats an
        // explicit undefined as a value, and a guarded card must have NO handler at all.
        ...(legal ? { onToggle: () => attack(id) } : {}),
      });
    }

    /**
     * Attack a target with the selected creature.
     *
     * ⚠️ `PerformAttackCmd` is a plain `Command` - no args model - so it needs ONLY the executor and
     * the targets. Sending a `MetaJson` it does not read is harmless; omitting the targets is not:
     * it throws `NoAvailableTargets`, which `CommandController` swallows.
     */
    function attack(targetId: number): void {
      if (attacker === null) return;
      // ⭐ The lunge is OURS to time - we know the instant we sent the command. The HIT and the DEATH
      // are the server's, and arrive as events.
      // ⭐ TWO SOUNDS, TWO MOMENTS: the arrow leaving, then the blow landing. The original names
      // them separately (`Arrow_Select`, `TableCard_Attack`) and collapsing them loses the weight.
      audio.play("arrowSelect", 0.7);
      audio.play("attack");
      // ⭐ THE ARROW FIRST, WHILE BOTH ENDPOINTS STILL EXIST. It reads their positions now and owns
      // nothing afterwards, so it survives the re-render (and the death) that follows the command.
      const fromEl = el.querySelector(`[data-obj="${attacker}"]`);
      const toEl = el.querySelector(`[data-obj="${targetId}"]`);
      const stage = el.closest<HTMLElement>(".stage");
      if (stage && fromEl && toEl) fireArrow(stage, fromEl, toEl);
      strike(attacker);
      send("PerformAttackCmd", {
        ExecutorObjectId: attacker,
        TargetObjectsIds: [targetId],
      });
      attacker = null;
    }

    /**
     * ⭐ **THE FOXFIRE SACRIFICE - OUR OWN MECHANIC, ON THE ENGINE'S OWN PRIMITIVE.**
     *
     * `SacrificeCardCmd` (`Revival.Core.decompiled.cs:18184`) does exactly two things:
     *
     *     RuntimePlayer.RuntimeData.Mana.Add(card.RuntimeData.Mana);
     *     card.ReturnToDiscard();
     *
     * i.e. burn a card from hand, gain its cost as Foxfire. Its guards are the engine's, not ours:
     * you cannot sacrifice another player's card, cannot do it once the timer has ended, and cannot
     * do it on someone else's turn.
     *
     * ⚠️ **THE AI DOES NOT PAY FOXFIRE, AND THAT IS AN OPEN STRUCTURAL ISSUE, NOT A CLIENT ONE.**
     * The bot goes through `CommandController.Execute` inside the engine binary and never reaches
     * our gate at `MatchSession.cs:972`. It cannot be fixed from here - see REBUILD-PLAN.md Phase 3.
     */
    function sacrificeById(objectId: number): void {
      audio.play("cardBurn");
      void Api.foxfireSacrifice(objectId)
        .then(() => refreshFoxfire())
        .catch((e) => {
          if (isMatchGone(e)) { showMatchGone(); return; }
          say(stageLine.textContent ?? "", `That card cannot be burned: ${describe(e)}`, true);
          status.classList.add("page__note--error");
        });
    }

    function sacrifice(c: RuntimeObject): void {
      audio.play("cardBurn");
      // 🚨 **THROUGH OUR ROUTE, NOT THE HUB COMMAND.** Sending `SacrificeCardCmd` straight to the
      // engine does only the engine's HALF - it discards the card and adds its scalar mana. The
      // TYPED token, which is the whole mechanic, is granted by `POST /Revival/Foxfire/Sacrifice`.
      // Measured 2026-08-14: the raw command burned the card (hand 6 -> 5) and granted nothing.
      void Api.foxfireSacrifice(c.Id ?? 0)
        .then(() => refreshFoxfire())
        .catch((e) => {
          if (isMatchGone(e)) { showMatchGone(); return; }
          say(stageLine.textContent ?? "", `That card cannot be burned: ${describe(e)}`, true);
          status.classList.add("page__note--error");
        });
    }

    function cardChip(c: RuntimeObject, inHand = false): HTMLElement {
      const id = c.Id ?? -1;

      if (inHand && model.timer.State === "Mulligan") {
        return cardEl(c, {
          selectable: true,
          selected: swapping.has(id),
          replacing: true,   // the ONLY place REPLACE is a real affordance
          onToggle: () => {
            if (swapping.has(id)) swapping.delete(id);
            else swapping.add(id);
            render();
          },
        });
      }

      // ⭐ PLAYING A CARD. Only from OUR hand, only on OUR turn, and only when we can pay for it.
      //
      // ⚠️ **THE AFFORDABILITY TEST HERE IS PRESENTATION, NOT A RULE.** `PlayCardCmd` re-checks it
      // and throws `"Not enough mana to play this card!"`; the server is the authority. Greying the
      // card out is so a player is not invited to press something that will be refused - it is not
      // the client deciding legality.
      if (inHand && model.ourTurn()) {
        // ⭐ **BOTH GATES, AND THEY ARE GENUINELY SEPARATE.** `Foxfire.cs` is explicit: *"The scalar
        // Foxfire COST is untouched, so PlayCardCmd's own check keeps working exactly as it did.
        // Colour is a SECOND, ADDITIVE gate that we apply in front of the engine."*
        //
        // ⛔ So `foxfire.playable` alone is NOT "can I play this" - it is the COLOUR half. Using it
        // by itself marked every card in hand playable at one Foxfire, including a five-cost card.
        // Measured 2026-08-14: PLAYABLE 6 of 6 with an empty pool.
        const cost = stat(c.Mana);
        const canAfford = cost <= stat(model.self()?.Mana);
        const fx = foxfire.hand?.find((h) => h.objectId === id);
        const colourOk = fx ? !!fx.playable : true;
        const affordable = canAfford && colourOk;

        // ⭐ WHICH gate refused, in the player's words. `fx.reason` is the server's, and it names the
        // colour and the shortfall ("needs 2 The_Verdant_Hollow Foxfire, holds 1"). Realm keys are
        // wire values, so the underscores come out before a human reads it.
        const refusal = !canAfford
          ? `Costs ${cost} Foxfire, you have ${stat(model.self()?.Mana)}`
          : !colourOk
            ? (fx?.reason ? String(fx.reason).replace(/_/g, " ") : "Needs Foxfire of this card's realm")
            : undefined;

        return cardEl(c, {
          selectable: true,
          playable: affordable,
          refusal,
          requires: fx?.requires,
          // ⭐ Burning is always available on our turn, even when the card is unaffordable - that is
          // the point of it. A hand full of cards you cannot pay for is exactly when you feed one to
          // the Foxfire.
          onBurn: () => sacrifice(c),
          // ⭐ DRAGGABLE INTO THE BASIN. The mechanic is spatial - you give a card TO the fire - so
          // the gesture should be too. The BURN button stays for keyboard and for a player who
          // would rather click.
          dragId: id,
          onToggle: () => {
            /* 🔴 **A CLICK ON THE HAND WHILE AIMING USED TO GO NOWHERE, AND IT READ AS A REFUSED
               PLAY.** Alex, 2026-08-18: *"I have just burnt 2 cards to be able to play the High
               Healer card, but it will not let me play it."* His screenshot shows the rail reading
               CHOOSE A TARGET with 4/5 Foxfire against a 4-cost card - he could afford it. The board
               was mid-aim and every other click was inert, and the only way out was a sentence in
               body text saying "Press ESC to cancel".

               ⭐ **THE PLAYER CHANGING THEIR MIND IS THE COMMONEST REASON TO CLICK ELSEWHERE**, so
               reaching for another card now cancels the aim and plays that card. The aimed card
               returns to hand exactly as ESC would leave it - nothing is spent. */
            if (aiming) resolveAim(null);
            if (!affordable) return;
            playCard(c);
          },
        });
      }

      return cardEl(c);
    }

    /**
     * Play a card from hand.
     *
     * ⚠️ **`ArgsModel` MUST NOT BE NULL.** `PlayCardCmd.OnExecute` throws `"Missing required args"`
     * outright (`Revival.Core.decompiled.cs:18051`), and `CommandController` SWALLOWS the throw - so
     * an omitted `MetaJson` is a card that simply never plays, with nothing on screen and nothing in
     * the client's log to say why. `PlayCardArgs` carries one field, `RelativePositionX`.
     *
     * ⚠️ The position is where on OUR row the card lands. Appending at the current width is the
     * honest default until the board supports dropping into a chosen slot.
     */
    /**
     * Play a card into a CHOSEN slot.
     *
     * ⚠️ **`RelativePositionX` IS THE SLOT THE PLAYER PICKED**, not the end of the row. The engine
     * has always taken it (`PlayCardArgs`); the first client appended, so placement - a real
     * decision in this game, because position matters for targeting and for taunts - was silently
     * removed from the player.
     *
     * ⚠️ `ArgsModel` must not be null: `PlayCardCmd.OnExecute` throws `"Missing required args"` and
     * `CommandController` swallows it, so an omitted `MetaJson` is a card that never plays with
     * nothing on screen to say why.
     */
    function playCardAt(objectId: number, slotIndex: number, targetIds: number[] = []): void {
      send("PlayCardCmd", {
        ExecutorObjectId: objectId,
        // 🚨 **THIS WAS ALWAYS `[]`, AND IT SILENTLY DISABLED 31 OF 171 CARDS.** The engine only
        // registers manual targets when `Targets.Length > 0` (`Command.Execute`, `:17587`), and it
        // then SKIPS every `PlayerPicked` effect that has none - an explicit branch at `:12220`, not
        // a fizzle. "Rally: Give target creature +1/+0" played as a vanilla body and said nothing.
        TargetObjectsIds: targetIds,
        MetaJson: JSON.stringify({ RelativePositionX: slotIndex }),
      });
    }

    /**
     * A card waiting for the player to aim it: the card, and the slot it will land in.
     *
     * ⚠️ The SLOT is captured at drop time, not at target time. The player chose where it goes
     * before they chose what it hits, and re-deriving the slot later would move their card.
     */
    let aiming: { objectId: number; slot: number; aim: EffectAim; legal: Set<number> } | null = null;

    /*
     * 🚨 **A TARGETED CARD NEEDED AN EMPTY SLOT BEFORE IT COULD BE AIMED.** DayDevil, beta feedback
     * 2026-08-20: *"treid to play a buf cards did not seem to go of. Seems buf card also need to be
     * played on a free field, then you need to click on a minion again. So the playing of cards
     * needs to be a bit more intuitive."*
     *
     * ⛔ **AND AT SEVEN CREATURES IT WAS UNPLAYABLE ENTIRELY.** The drop listener was registered
     * only inside the `else` branch that marks a slot `slot--empty`, so with a full board there was
     * no drop target at all for a spell that does not occupy a slot. The click fallback was no
     * escape either: it passes `zone("InTable").length` as the slot, which at 7 is out of range and
     * the engine answers `TableIsFull`.
     *
     * ⭐ **THE FIX IS THE GESTURE HE EXPECTED: DRAG THE BUFF ONTO THE CREATURE.** One movement
     * instead of drop-then-click, and it removes the empty-slot requirement rather than working
     * around it. `dragTarget` is what lets an OCCUPIED slot decide whether to accept the drag, since
     * `dataTransfer` cannot be read during `dragover` in any browser we support.
     */
    let dragging: { objectId: number; aim: EffectAim | undefined; legal: Set<number> } | null = null;

    function beginDrag(objectId: number): void {
      const o = model.objects.get(objectId);
      const aim = aimFor(o?.DataId);
      const legal = aim
        ? new Set(legalPlayTargets(aim, model.selfId, model.objects.values()).map((t) => t.Id ?? -1))
        : new Set<number>();
      dragging = { objectId, aim, legal };
    }
    function endDrag(): void { dragging = null; }

    /**
     * Mark the legal targets, AFTER the board has been drawn.
     *
     * 🚨 **THIS IS WHY THE FEATURE LOOKED COMPLETELY DEAD.** The first version set `card--aimable`
     * and THEN called `render()`, which rebuilds the board and threw every class away. So the card
     * was correctly held back waiting to be aimed, nothing was highlighted, and no `PlayCardCmd`
     * ever reached the server - which reads exactly like "the play was refused". The gate said
     * REFUSED, and the truth was "armed, and invisible".
     *
     * ⚠️ **SECOND TIME IN ONE SESSION.** The emote had the identical shape: an element attached to a
     * subtree that the very next render replaced. **State that must survive a render has to be
     * re-applied BY the render, not set alongside it.** Anything set before `render()` is decoration
     * on a corpse.
     */
    function markAimable(): void {
      const legal = aiming?.legal;
      for (const node of el.querySelectorAll<HTMLElement>("[data-obj]")) {
        const id = Number(node.dataset["obj"]);
        node.classList.toggle("card--aimable", !!legal && legal.has(id));
      }
    }

    /**
     * Play a card, pausing to aim it first when it needs a target.
     *
     * ⭐ **THE PAUSE IS THE FEATURE.** The alternative - auto-picking a legal target - is what the
     * engine's `Random` mod already does for cards that WANT randomness, and doing it for a
     * `PlayerPicked` card takes the decision away from the player on exactly the cards written to
     * give them one.
     *
     * ⚠️ **NO LEGAL TARGET MEANS PLAY IT ANYWAY, UNAIMED.** A creature with a Rally that has nothing
     * to hit is still a body, and refusing the play would strand the card in hand for the rest of
     * the match with no explanation.
     */
    function playWithAim(objectId: number, slot: number, dataId: string | undefined): void {
      const aim = aimFor(dataId);
      if (!aim) { playCardAt(objectId, slot); return; }

      const targets = legalPlayTargets(aim, model.selfId, model.objects.values());
      if (targets.length === 0) {
        say(stageLine.textContent ?? "", "Nothing to aim at, so it comes down on its own.");
        playCardAt(objectId, slot);
        return;
      }

      aiming = { objectId, slot, aim, legal: new Set(targets.map((t) => t.Id ?? -1)) };
      // ⚠️ Both the rail text and the highlight are applied INSIDE `render` - see `markAimable`
      // and the `if (aiming)` branch in the guidance block. Writing them here would be undone by
      // the very next render.
      render();
    }

    /** Finish an aimed play, or cancel it. */
    function resolveAim(targetId: number | null): void {
      const a = aiming;
      aiming = null;
      markAimable();
      if (!a) return;
      if (targetId === null) {
        // ⚠️ Cancelled: the card stays IN HAND. Playing it unaimed on a cancel would spend it on
        // nothing, which is the worst possible reading of "escape".
        say(stageLine.textContent ?? "", "Cancelled. The card is still in your hand.");
        render();
        return;
      }
      playCardAt(a.objectId, a.slot, [targetId]);
    }

    // ⭐ **DELEGATED, IN THE CAPTURE PHASE, SO EVERY RENDER PATH IS COVERED AND NONE OF THEM KNOWS.**
    //
    // ⚠️ Capture matters: a board card already has its own click handler (select an attacker, use
    // the hero power). While aiming, this must win - otherwise clicking a target would ALSO arm an
    // attack, and the player would end up doing two things having asked for one.
    el.addEventListener("click", (e) => {
      if (!aiming) return;
      const hit = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-obj]");

      /* 🚨 **AIMING WAS A TRAP, AND IT IS THE "MY CARD DISAPPEARED" REPORT.** Alex, repeatedly:
         *"I clicked a card and it just disappeared, it didn't go to the play area"*, *"no idea what
         happened"*.

         Measured by playing the game rather than reading it (`probe_act.mjs`):

             after playing   : stage CHOOSE A TARGET, action button = CANCEL
             after selecting : stage CHOOSE A TARGET, action button = CANCEL
             after hero click: stage CHOOSE A TARGET, action button = CANCEL

         ⛔ **THE CARD LEAVES THE HAND, END TURN IS REPLACED BY CANCEL, AND EVERY CLICK THAT IS NOT
         A LEGAL TARGET IS SWALLOWED IN SILENCE.** So a player who does not notice the highlight has
         no card, no turn button, and no message - which is precisely "the game is frozen" and
         "no idea what happened". The `return` on this line was that silence.

         ⭐ **A CLICK SOMEWHERE ELSE IS THE PLAYER TELLING YOU THEY DID NOT MEAN THIS.** It now says
         what is being aimed and what to do, and a click on the empty board cancels outright, so the
         state can never be entered without an exit the player can find. */
      if (!hit || !hit.classList.contains("card--aimable")) {
        const what = cardName(model.objects.get(aiming.objectId)?.DataId) || "That card";
        if (hit) {
          // They clicked a REAL thing that simply is not legal for this card.
          say(stageLine.textContent ?? "",
              `${what} cannot target that. Tap a highlighted card, or press CANCEL to take it back.`,
              true);
        } else {
          // They clicked the table. Treat it as "I have changed my mind" and give the card back.
          resolveAim(null);
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const id = Number(hit.dataset["obj"]);
      if (Number.isFinite(id)) {
        audio.play("cardDrop");
        resolveAim(id);
      }
    }, true);

    /* ⭐ **DELEGATED, LIKE THE CLICK HANDLER, so no render path has to remember to wire it.** Every
     * card carries `data-obj`, so the board can learn what is in flight without `card.ts` knowing
     * anything about aiming. */
    el.addEventListener("dragstart", (e) => {
      const node = (e.target as HTMLElement | null)?.closest<HTMLElement>("[data-obj]");
      const id = Number(node?.dataset["obj"]);
      if (Number.isFinite(id) && id > 0) beginDrag(id);
    }, true);
    el.addEventListener("dragend", () => endDrag(), true);

    // ⚠️ ESC cancels and the card stays in hand - see `resolveAim`. Registered on the SCREEN, not the
    // window, so leaving the match takes it with us.
    el.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && aiming) { e.preventDefault(); resolveAim(null); }
    });
    el.tabIndex = -1;

    function playCard(c: RuntimeObject): void {
      // Keyboard and click fallback: append at the current row width. Drag is the real mechanic.
      const used = model.zone(model.selfId, "InTable").length;
      /* ⛔ **A FULL ROW MUST REFUSE A CREATURE AND STILL ALLOW A SPELL.** `used` at 7 is not a legal
       * slot, and `PlayCardCmd` answers `TableIsFull` - which the player sees as a card that does
       * nothing. A spell does not take a slot, so only the creature is actually blocked, and it is
       * told why instead of failing silently. */
      const isCreature = String(cardRecord(c.DataId)?.["type"] ?? "") === "Creature";
      if (isCreature && used >= SLOTS_PER_SIDE) {
        say(stageLine.textContent ?? "", "Your row is full, so there is nowhere for it to stand.", true);
        return;
      }
      playWithAim(c.Id ?? 0, Math.min(used, SLOTS_PER_SIDE - 1), c.DataId);
    }
  },

  unmount(): void {
    // ⛔ WITHDRAW THE DIAGNOSTIC CONTEXT. A provider that outlives its screen makes every later
    // bug report carry a board the player already left, which is worse than carrying none: it reads
    // as evidence and points the reader at the wrong match.
    clearContext("match");


    // ⚠️ The overlay is appended to <body>, so it does NOT leave with the screen. Left open, it
    // would float over the menu.
    closeSettings();
    cancelBoardKeys?.();
    void hub?.stop();
    hub = null;
  },
};

function describe(e: unknown): string {
  if (e instanceof ApiError) return e.body?.trim() ? e.body.slice(0, 300) : `${e.status} ${e.path}`;
  return e instanceof Error ? e.message : String(e);
}

let hub: GameHubClient | null = null;

/**
 * Releases the board's window-level key listener.
 *
 * ⚠️ Module-level because `unmount` runs outside the mount closure. A key listener that outlives its
 * screen keeps interpreting Escape as "cancel the hero power" on the menu.
 */
let cancelBoardKeys: (() => void) | null = null;

/**
 * Slots a player may have on the table at once.
 *
 * ⚠️ **THIS IS `maxCardsOnTable` FROM THE SERVED CONFIG (7), NOT A LAYOUT CHOICE.** The engine
 * enforces it - `PlayCardCmd` throws `TableIsFull` - so a board drawing a different number would
 * either hide a legal slot or offer one that can never be filled. A board THEME must never change
 * it; that would make a bought skin alter the game.
 */
const SLOTS_PER_SIDE = 7;


